<template>
  <div id="app">
    <!-- Station Detail Popup - full device info for one store (Station Overview cards drill into this) -->
    <div v-if="showStationDetail && stationDetail" class="simple-popup" @click.self="showStationDetail = false">
      <div class="popup-content">
        <div class="popup-header">
          <h3>{{ stationDetail.storeName }} ({{ stationDetail.devices.length }} device{{ stationDetail.devices.length === 1 ? '' : 's' }})</h3>
          <button @click="showStationDetail = false" class="close-btn">×</button>
        </div>
        <div class="popup-body">
          <div v-for="(dev, idx) in stationDetail.devices" :key="dev.deviceId">
            <div class="popup-section-label" :class="{ 'station-card-section-label': idx === 0 }">
              {{ dev.deviceAlias || dev.deviceId }} <span class="device-alias">({{ dev.deviceId }})</span>
            </div>
            <div class="station-card-updated" v-if="dev.lastUpdated">Last updated: {{ formatDateTime(dev.lastUpdated) }}</div>

            <div class="telemetry-details-grid">
              <div class="telemetry-field" v-if="dev.fields.AppVer">
                <span class="telemetry-label">App Version</span>
                <span class="telemetry-value">{{ dev.fields.AppVer }}</span>
              </div>
              <div class="telemetry-field" v-if="dev.fields.IdfVer">
                <span class="telemetry-label">IDF Version</span>
                <span class="telemetry-value">{{ dev.fields.IdfVer }}</span>
              </div>
              <div class="telemetry-field" v-if="dev.fields.GitIdfVer">
                <span class="telemetry-label">Git IDF Version</span>
                <span class="telemetry-value">{{ dev.fields.GitIdfVer }}</span>
              </div>
              <div class="telemetry-field" v-for="tank in dev.fields.Tanks" :key="'tank-' + tank.Id">
                <span class="telemetry-label">Tank #{{ tank.Id }}</span>
                <span class="telemetry-value">{{ tank.Prod }}<span v-if="tank.Code"> ({{ tank.Code }})</span><span v-if="tank.IoType"> · IO Type {{ tank.IoType }}</span></span>
              </div>
              <div class="telemetry-field" v-if="typeof dev.fields.Uptime === 'number'">
                <span class="telemetry-label">Uptime (last session)</span>
                <span class="telemetry-value">{{ formatUptime(dev.fields.Uptime) }}</span>
              </div>
              <div class="telemetry-field" v-if="typeof dev.fields.HeapFreeInt === 'number'">
                <span class="telemetry-label">Heap Free (Internal)</span>
                <span class="telemetry-value" :class="{ 'telemetry-warning': isLowMemory(dev.fields) }">{{ formatBytes(dev.fields.HeapFreeInt) }}</span>
              </div>
              <div class="telemetry-field" v-if="typeof dev.fields.HeapMinInt === 'number'">
                <span class="telemetry-label">Heap Min (Internal)</span>
                <span class="telemetry-value">{{ formatBytes(dev.fields.HeapMinInt) }}</span>
              </div>
              <div class="telemetry-field" v-if="typeof dev.fields.HeapFreeSpi === 'number'">
                <span class="telemetry-label">Heap Free (SPI)</span>
                <span class="telemetry-value">{{ formatBytes(dev.fields.HeapFreeSpi) }}</span>
              </div>
              <div class="telemetry-field" v-if="typeof dev.fields.HeapMinSpi === 'number'">
                <span class="telemetry-label">Heap Min (SPI)</span>
                <span class="telemetry-value">{{ formatBytes(dev.fields.HeapMinSpi) }}</span>
              </div>
              <div class="telemetry-field" v-if="typeof dev.fields.WifiEnabled === 'boolean'">
                <span class="telemetry-label">WiFi Enabled</span>
                <span class="telemetry-value">{{ dev.fields.WifiEnabled ? 'Yes' : 'No' }}</span>
              </div>
              <div class="telemetry-field" v-if="typeof dev.fields.WifiRssi === 'number'">
                <span class="telemetry-label">WiFi Signal</span>
                <span class="telemetry-value" :class="{ 'telemetry-warning': isWeakSignal(dev.fields) }">{{ dev.fields.WifiRssi }} dBm</span>
              </div>
              <div class="telemetry-field" v-if="dev.fields.WifiSSID">
                <span class="telemetry-label">WiFi Network</span>
                <span class="telemetry-value">{{ dev.fields.WifiSSID }}</span>
              </div>
              <div class="telemetry-field" v-if="typeof dev.fields.GsmEnabled === 'boolean'">
                <span class="telemetry-label">GSM Enabled</span>
                <span class="telemetry-value">{{ dev.fields.GsmEnabled ? 'Yes' : 'No' }}</span>
              </div>
              <div class="telemetry-field" v-if="hasGsmSignal(dev.fields)">
                <span class="telemetry-label">GSM Signal</span>
                <span class="telemetry-value">{{ dev.fields.GsmRssi }} dBm</span>
              </div>
              <div class="telemetry-field" v-if="dev.fields.GsmOper">
                <span class="telemetry-label">GSM Operator</span>
                <span class="telemetry-value">{{ cleanGsmOper(dev.fields.GsmOper) }}</span>
              </div>
              <div class="telemetry-field" v-if="typeof dev.fields.GsmTech === 'number'">
                <span class="telemetry-label">GSM Tech</span>
                <span class="telemetry-value">{{ getGsmTechLabel(dev.fields.GsmTech) }}</span>
              </div>
              <div class="telemetry-field" v-if="dev.fields.VpnStatus">
                <span class="telemetry-label">VPN Status</span>
                <span class="telemetry-value" :class="{ 'telemetry-warning': dev.fields.VpnStatus !== 'Up' }">{{ dev.fields.VpnStatus }}</span>
              </div>
              <div class="telemetry-field" v-if="dev.fields.VpnIp">
                <span class="telemetry-label">VPN IP</span>
                <span class="telemetry-value">{{ dev.fields.VpnIp }}</span>
              </div>
              <div class="telemetry-field" v-if="typeof dev.fields.FramUnposted === 'number'">
                <span class="telemetry-label">FRAM Unposted</span>
                <span class="telemetry-value" :class="{ 'telemetry-warning': dev.fields.FramUnposted > 0 }">{{ dev.fields.FramUnposted }}</span>
              </div>
            </div>

            <div class="nozzle-detail-list" v-if="dev.nozzles && dev.nozzles.length">
              <div class="nozzle-detail-card" v-for="n in dev.nozzles" :key="n.Noz">
                <div class="nozzle-detail-header">
                  <span class="nozzle-badge">⛽ Nozzle {{ n.Noz }}</span>
                  <span class="nozzle-detail-time">{{ formatDateTime(n.reportedAt) }}</span>
                </div>
                <div class="nozzle-detail-body">
                  <div class="nozzle-detail-item">
                    <span class="nozzle-detail-label">Volume</span>
                    <span class="nozzle-detail-value">{{ typeof n.Liters === 'number' ? n.Liters.toFixed(2) + ' L' : '-' }}</span>
                  </div>
                  <div class="nozzle-detail-item" v-if="n.Reg">
                    <span class="nozzle-detail-label">Registration</span>
                    <span class="nozzle-detail-value">{{ n.Reg }}</span>
                  </div>
                  <div class="nozzle-detail-item" v-if="typeof n.Tot === 'number'">
                    <span class="nozzle-detail-label">Totaliser</span>
                    <span class="nozzle-detail-value">{{ n.Tot.toLocaleString() }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Device Breakdown Popup - drilldown from the Statistics Overview cards -->
    <div v-if="showDeviceBreakdown" class="simple-popup" @click.self="showDeviceBreakdown = false">
      <div class="popup-content">
        <div class="popup-header">
          <h3>{{ deviceBreakdownTitle }} ({{ deviceBreakdownRows.length }})</h3>
          <button @click="showDeviceBreakdown = false" class="close-btn">×</button>
        </div>
        <div class="popup-body">
          <div class="device-list-container">
            <div v-for="row in deviceBreakdownRows" :key="row.deviceId" class="device-list-item">
              <div class="device-main-info">
                <strong>{{ row.deviceId }}</strong>
                <span class="device-alias">{{ row.deviceAlias || 'No alias' }}</span>
              </div>
              <div class="device-stats">
                <span class="device-stat-badge" v-if="row.storeName">{{ row.storeName }}</span>
                <span class="device-stat-badge" :class="row.badgeClass">{{ row.badgeLabel }}</span>
                <span class="device-stat-badge" v-if="row.detailLabel">{{ row.detailLabel }}</span>
              </div>
              <div class="nozzle-detail-list" v-if="row.nozzleDetails && row.nozzleDetails.length">
                <div class="nozzle-detail-card" v-for="n in row.nozzleDetails" :key="n.noz">
                  <div class="nozzle-detail-header">
                    <span class="nozzle-badge">⛽ Nozzle {{ n.noz }}</span>
                    <span class="nozzle-detail-time">{{ n.time }}</span>
                  </div>
                  <div class="nozzle-detail-body">
                    <div class="nozzle-detail-item">
                      <span class="nozzle-detail-label">Volume</span>
                      <span class="nozzle-detail-value">{{ n.volume }}</span>
                    </div>
                    <div class="nozzle-detail-item" v-if="n.reg">
                      <span class="nozzle-detail-label">Registration</span>
                      <span class="nozzle-detail-value">{{ n.reg }}</span>
                    </div>
                    <div class="nozzle-detail-item" v-if="n.total">
                      <span class="nozzle-detail-label">Totaliser</span>
                      <span class="nozzle-detail-value">{{ n.total }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="device-charts" v-if="deviceBreakdownType === 'memory' && row.heapFreeInt != null">
                <div class="device-chart-row">
                  <span class="device-chart-label">Heap {{ formatBytes(row.heapFreeInt) }} free</span>
                  <div class="device-chart-bar-track">
                    <div class="device-chart-bar-fill" :class="'bar-' + memoryLevel({ HeapFreeInt: row.heapFreeInt })" :style="{ width: getMemoryBarPercent(row.heapFreeInt) + '%' }"></div>
                  </div>
                </div>
              </div>
              <div class="device-charts" v-else-if="deviceBreakdownType === 'signal' && row.wifiRssi != null">
                <div class="device-chart-row">
                  <span class="device-chart-label">WiFi {{ row.wifiRssi }}dBm</span>
                  <div class="device-chart-bar-track">
                    <div class="device-chart-bar-fill" :class="row.wifiRssi <= -75 ? 'bar-weak' : 'bar-strong'" :style="{ width: getSignalBarPercent(row.wifiRssi) + '%' }"></div>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="deviceBreakdownRows.length === 0" class="no-devices">
              No devices with data for this view in the selected range.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 1. ADD THIS TO YOUR TEMPLATE (right after opening <div id="app">) -->
    <!-- Simple Popup Modal -->
    <div v-if="selectedActivity" class="simple-popup" @click.self="selectedActivity = null">
      <div class="popup-content">
        <div class="popup-header">
          <h3>Activity Details</h3>
          <button @click="selectedActivity = null" class="close-btn">×</button>
        </div>
        <div class="popup-body">
          <div class="telemetry-details-grid">
            <div class="telemetry-field">
              <span class="telemetry-label">Date</span>
              <span class="telemetry-value">{{ formatDateTime(selectedActivity.timestamp || selectedActivity.createDate) }}</span>
            </div>
            <div class="telemetry-field">
              <span class="telemetry-label">Device ID</span>
              <span class="telemetry-value">{{ selectedActivity.deviceId }}</span>
            </div>
            <div class="telemetry-field">
              <span class="telemetry-label">Device Alias</span>
              <span class="telemetry-value">{{ selectedActivity.deviceAlias || '-' }}</span>
            </div>
            <div class="telemetry-field">
              <span class="telemetry-label">Store</span>
              <span class="telemetry-value">{{ selectedActivity.storeName || '-' }}</span>
            </div>
            <div class="telemetry-field">
              <span class="telemetry-label">Account</span>
              <span class="telemetry-value">{{ selectedActivity.accountName || '-' }}</span>
            </div>
            <div class="telemetry-field">
              <span class="telemetry-label">Activity Type</span>
              <span class="telemetry-value"><span :class="['activity-badge', 'badge-' + selectedActivity.type]">{{ selectedActivity.type }}</span></span>
            </div>
            <div class="telemetry-field">
              <span class="telemetry-label">Status</span>
              <span class="telemetry-value"><span :class="'status-' + selectedActivity.status">{{ selectedActivity.status }}</span></span>
            </div>
          </div>

          <div class="popup-section-label">Message</div>
          <div class="message-detail-row">
            <div v-if="selectedActivity.telemetryFields" class="telemetry-details-grid">
              <div class="telemetry-field" v-if="selectedActivity.telemetryFields.AppVer">
                <span class="telemetry-label">App Version</span>
                <span class="telemetry-value">{{ selectedActivity.telemetryFields.AppVer }}</span>
              </div>
              <div class="telemetry-field" v-if="selectedActivity.telemetryFields.IdfVer">
                <span class="telemetry-label">IDF Version</span>
                <span class="telemetry-value">{{ selectedActivity.telemetryFields.IdfVer }}</span>
              </div>
              <div class="telemetry-field" v-if="selectedActivity.telemetryFields.GitIdfVer">
                <span class="telemetry-label">Git IDF Version</span>
                <span class="telemetry-value">{{ selectedActivity.telemetryFields.GitIdfVer }}</span>
              </div>
              <div class="telemetry-field" v-for="tank in selectedActivity.telemetryFields.Tanks" :key="'tank-' + tank.Id">
                <span class="telemetry-label">Tank #{{ tank.Id }}</span>
                <span class="telemetry-value">{{ tank.Prod }}<span v-if="tank.Code"> ({{ tank.Code }})</span><span v-if="tank.IoType"> · IO Type {{ tank.IoType }}</span></span>
              </div>
              <div class="telemetry-field" v-if="typeof selectedActivity.telemetryFields.Uptime === 'number'">
                <span class="telemetry-label">Uptime (last session)</span>
                <span class="telemetry-value">{{ formatUptime(selectedActivity.telemetryFields.Uptime) }}</span>
              </div>
              <div class="telemetry-field" v-if="typeof selectedActivity.telemetryFields.HeapFreeInt === 'number'">
                <span class="telemetry-label">Heap Free (Internal)</span>
                <span class="telemetry-value" :class="{ 'telemetry-warning': isLowMemory(selectedActivity.telemetryFields) }">{{ formatBytes(selectedActivity.telemetryFields.HeapFreeInt) }}</span>
              </div>
              <div class="telemetry-field" v-if="typeof selectedActivity.telemetryFields.HeapMinInt === 'number'">
                <span class="telemetry-label">Heap Min (Internal)</span>
                <span class="telemetry-value">{{ formatBytes(selectedActivity.telemetryFields.HeapMinInt) }}</span>
              </div>
              <div class="telemetry-field" v-if="typeof selectedActivity.telemetryFields.HeapFreeSpi === 'number'">
                <span class="telemetry-label">Heap Free (SPI)</span>
                <span class="telemetry-value">{{ formatBytes(selectedActivity.telemetryFields.HeapFreeSpi) }}</span>
              </div>
              <div class="telemetry-field" v-if="typeof selectedActivity.telemetryFields.HeapMinSpi === 'number'">
                <span class="telemetry-label">Heap Min (SPI)</span>
                <span class="telemetry-value">{{ formatBytes(selectedActivity.telemetryFields.HeapMinSpi) }}</span>
              </div>
              <div class="telemetry-field" v-if="typeof selectedActivity.telemetryFields.WifiEnabled === 'boolean'">
                <span class="telemetry-label">WiFi Enabled</span>
                <span class="telemetry-value">{{ selectedActivity.telemetryFields.WifiEnabled ? 'Yes' : 'No' }}</span>
              </div>
              <div class="telemetry-field" v-if="typeof selectedActivity.telemetryFields.WifiRssi === 'number'">
                <span class="telemetry-label">WiFi Signal</span>
                <span class="telemetry-value" :class="{ 'telemetry-warning': isWeakSignal(selectedActivity.telemetryFields) }">{{ selectedActivity.telemetryFields.WifiRssi }} dBm</span>
              </div>
              <div class="telemetry-field" v-if="selectedActivity.telemetryFields.WifiSSID">
                <span class="telemetry-label">WiFi Network</span>
                <span class="telemetry-value">{{ selectedActivity.telemetryFields.WifiSSID }}</span>
              </div>
              <div class="telemetry-field" v-if="typeof selectedActivity.telemetryFields.GsmEnabled === 'boolean'">
                <span class="telemetry-label">GSM Enabled</span>
                <span class="telemetry-value">{{ selectedActivity.telemetryFields.GsmEnabled ? 'Yes' : 'No' }}</span>
              </div>
              <div class="telemetry-field" v-if="hasGsmSignal(selectedActivity.telemetryFields)">
                <span class="telemetry-label">GSM Signal</span>
                <span class="telemetry-value">{{ selectedActivity.telemetryFields.GsmRssi }} dBm</span>
              </div>
              <div class="telemetry-field" v-if="selectedActivity.telemetryFields.GsmOper">
                <span class="telemetry-label">GSM Operator</span>
                <span class="telemetry-value">{{ cleanGsmOper(selectedActivity.telemetryFields.GsmOper) }}</span>
              </div>
              <div class="telemetry-field" v-if="typeof selectedActivity.telemetryFields.GsmTech === 'number'">
                <span class="telemetry-label">GSM Tech</span>
                <span class="telemetry-value">{{ getGsmTechLabel(selectedActivity.telemetryFields.GsmTech) }}</span>
              </div>
              <div class="telemetry-field" v-if="selectedActivity.telemetryFields.VpnStatus">
                <span class="telemetry-label">VPN Status</span>
                <span class="telemetry-value" :class="{ 'telemetry-warning': selectedActivity.telemetryFields.VpnStatus !== 'Up' }">{{ selectedActivity.telemetryFields.VpnStatus }}</span>
              </div>
              <div class="telemetry-field" v-if="selectedActivity.telemetryFields.VpnIp">
                <span class="telemetry-label">VPN IP</span>
                <span class="telemetry-value">{{ selectedActivity.telemetryFields.VpnIp }}</span>
              </div>
              <div class="telemetry-field" v-if="typeof selectedActivity.telemetryFields.FramUnposted === 'number'">
                <span class="telemetry-label">FRAM Unposted</span>
                <span class="telemetry-value" :class="{ 'telemetry-warning': selectedActivity.telemetryFields.FramUnposted > 0 }">{{ selectedActivity.telemetryFields.FramUnposted }}</span>
              </div>
              <div class="telemetry-lastdisp" v-if="selectedActivity.telemetryFields.LastDisp && selectedActivity.telemetryFields.LastDisp.length">
                <span class="telemetry-label">Last Dispense (last session) ({{ selectedActivity.telemetryFields.LastDisp.length }})</span>
                <div class="telemetry-lastdisp-row" v-for="(disp, idx) in selectedActivity.telemetryFields.LastDisp" :key="idx">
                  Nozzle {{ disp.Noz }} · Reg {{ disp.Reg }}<span v-if="disp.EqpTag"> ({{ disp.EqpTag }})</span> · {{ (disp.Liters || 0).toFixed(2) }}L · Total {{ disp.Tot }}
                </div>
              </div>
            </div>
            <pre class="message-text" v-else-if="isJsonMessage(selectedActivity.message)">{{ prettyMessage(selectedActivity.message) }}</pre>
            <div class="message-text message-text-plain" v-else>{{ selectedActivity.message }}</div>
          </div>
        </div>
      </div>
    </div>
    <!-- Header -->
    <div class="header-toolbar">
      <div class="header-content">
        <div class="logo-section">
          <div class="logo-icon">
            <span class="icon-pulse">⚡</span>
          </div>
          <h1 class="dashboard-title">FAMS Device Startup Activity Dashboard</h1>
        </div>
        <div class="header-controls">
          <div class="loader-controls">
            <div class="loader-toggle">
              <label class="toggle-label">
                <input type="checkbox" v-model="showLoaderOption" class="toggle-checkbox">
                <span class="toggle-text">Show Loader</span>
              </label>
            </div>
            <div v-if="showLoaderOption" class="loader-speed">
              <label class="speed-label">
                <span class="speed-text">Speed:</span>
                <select v-model="minLoaderTime" class="speed-select">
                  <option :value="0">Instant</option>
                  <option :value="500">Fast (0.5s)</option>
                  <option :value="1000">Normal (1s)</option>
                  <option :value="1500">Smooth (1.5s)</option>
                  <option :value="2000">Slow (2s)</option>
                  <option :value="3000">Very Slow (3s)</option>
                </select>
              </label>
            </div>
            <div class="auto-refresh-toggle">
              <label class="toggle-label">
                <input type="checkbox" v-model="autoRefreshEnabled" @change="handleAutoRefreshToggle" class="toggle-checkbox">
                <span class="toggle-text">Auto Refresh</span>
              </label>
            </div>
            <div v-if="autoRefreshEnabled" class="refresh-interval">
              <label class="interval-label">
                <span class="interval-text">Every:</span>
                <select v-model="refreshInterval" @change="handleRefreshIntervalChange" class="interval-select">
                  <option :value="15000">15 sec</option>
                  <option :value="30000">30 sec</option>
                  <option :value="60000">1 min</option>
                  <option :value="300000">5 min</option>
                  <option :value="600000">10 min</option>
                  <option :value="1800000">30 min</option>
                </select>
              </label>
            </div>
          </div>
          <div class="live-indicator">
            <div :class="['live-dot', { 'error': errorCount >= 3, 'auto-refresh': autoRefreshEnabled }]"></div>
            <span style="color: white; font-size: 0.875rem;">
              {{ autoRefreshEnabled ? `Live (${formatRefreshInterval()})` : connectionStatus }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Global Loader Overlay -->
    <div v-if="showLoaderOption && isLoading" class="global-loader-overlay">
      <div class="global-loader-content">
        <div class="global-loader"></div>
        <div class="loader-text">{{ loaderMessage }}</div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="main-content">
      <!-- Statistics Section -->
      <div class="stats-section">
        <div class="stats-header">
          <h3 class="stats-title">Statistics Overview</h3>
          <button class="toggle-stats-btn" @click="statsVisible = !statsVisible">
            <span class="toggle-icon">{{ statsVisible ? '📊' : '📈' }}</span>
            {{ statsVisible ? 'Hide Statistics' : 'Show Statistics' }}
          </button>
        </div>

        <transition name="slide-fade">
          <div v-if="statsVisible" class="stats-wrapper">
            <!-- Statistics Cards -->
            <div class="stats-container">
              <div class="stat-card clickable-stat" @click="openMemoryBreakdown" title="Click to see devices by memory health">
                <div class="stat-label">Devices by Memory Health</div>
                <div class="stat-value">{{ stats.totalDevicesWithActivity || 0 }}</div>
                <div class="stat-change" :class="(stats.memoryCritical || 0) > 0 ? 'negative' : 'positive'">
                  <span class="stat-icon">{{ (stats.memoryCritical || 0) > 0 ? '⚠' : '✓' }}</span>
                  <span>{{ stats.memoryCritical || 0 }} critical · {{ stats.memoryLow || 0 }} low · {{ stats.memoryOk || 0 }} ok<template v-if="stats.memoryUnknown"> · {{ stats.memoryUnknown }} no data</template></span>
                </div>
              </div>

              <div class="stat-card clickable-stat" @click="openSignalBreakdown" title="Click to see devices by signal strength">
                <div class="stat-label">Devices by Signal Strength</div>
                <div class="stat-value">{{ stats.totalDevicesWithActivity || 0 }}</div>
                <div class="stat-change" :class="(stats.signalWeak || 0) > 0 ? 'negative' : 'positive'">
                  <span class="stat-icon">{{ (stats.signalWeak || 0) > 0 ? '📶' : '✓' }}</span>
                  <span>{{ stats.signalWeak || 0 }} weak · {{ stats.signalStrong || 0 }} strong<template v-if="stats.signalUnknown"> · {{ stats.signalUnknown }} no data</template></span>
                </div>
              </div>

              <div class="stat-card clickable-stat" @click="openFirmwareBreakdown" title="Click to see devices by firmware version">
                <div class="stat-label">Firmware Version Spread</div>
                <div class="stat-value">{{ stats.firmwareVersionCount || 0 }}</div>
                <div class="stat-change" :class="(stats.firmwareVersionCount || 0) > 1 ? 'negative' : 'positive'">
                  <span class="stat-icon">🔧</span>
                  <span>{{ getFirmwareSpreadLabel() }}</span>
                </div>
              </div>

              <div class="stat-card clickable-stat" @click="openVpnBreakdown" title="Click to see devices by VPN status">
                <div class="stat-label">VPN Status</div>
                <div class="stat-value">{{ stats.vpnConfiguredCount || 0 }}</div>
                <div class="stat-change" :class="(stats.vpnDown || 0) > 0 ? 'negative' : 'positive'">
                  <span class="stat-icon">{{ (stats.vpnDown || 0) > 0 ? '⚠' : '✓' }}</span>
                  <span v-if="stats.vpnConfiguredCount > 0">{{ stats.vpnUp || 0 }} up · {{ stats.vpnDown || 0 }} down<template v-if="stats.vpnMaybeUp"> · {{ stats.vpnMaybeUp }} maybe up</template></span>
                  <span v-else>No devices have VPN configured</span>
                </div>
              </div>

              <div class="stat-card clickable-stat" @click="openFramBreakdown" title="Click to see devices by pending sync backlog">
                <div class="stat-label">Pending Sync (FRAM)</div>
                <div class="stat-value">{{ stats.totalDevicesWithActivity || 0 }}</div>
                <div class="stat-change" :class="(stats.framPending || 0) > 0 ? 'negative' : 'positive'">
                  <span class="stat-icon">{{ (stats.framPending || 0) > 0 ? '⏳' : '✓' }}</span>
                  <span>{{ stats.framPending || 0 }} pending · {{ stats.framOk || 0 }} synced<template v-if="stats.framUnknown"> · {{ stats.framUnknown }} no data</template></span>
                </div>
              </div>

              <div class="stat-card clickable-stat" @click="openLastTransactionBreakdown" title="Click to see each device/store's last transaction">
                <div class="stat-label">Last Transaction</div>
                <div class="stat-value">{{ stats.totalDevicesWithActivity || 0 }}</div>
                <div class="stat-change" :class="(stats.noTransactionCount || 0) > 0 ? 'negative' : 'positive'">
                  <span class="stat-icon">🧾</span>
                  <span>{{ stats.withTransactionCount || 0 }} with data<template v-if="stats.noTransactionCount"> · {{ stats.noTransactionCount }} no data</template></span>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <!-- Station Overview -->
      <div class="grid-container">
        <div class="grid-header">
          <div class="grid-title">
            Station Overview
          </div>
        </div>

        <div class="station-cards-container">
          <div class="station-card station-card-clickable" v-for="station in stationCards" :key="station.storeName" @click="openStationDetail(station)" title="Click to view full device information">
            <div class="station-card-header">
              <strong>{{ station.storeName }}</strong>
              <span class="device-stat-badge" v-for="dev in station.devices" :key="dev.deviceId">{{ dev.deviceAlias || dev.deviceId }}</span>
            </div>

            <div class="station-card-updated" v-if="station.lastUpdatedAt">Last updated: {{ formatDateTime(station.lastUpdatedAt) }}</div>

            <div class="device-stats station-card-badges">
              <span class="device-stat-badge"
                :class="{ 'status-error': station.memoryLevel === 'critical', 'status-pending': station.memoryLevel === 'low', 'status-processed': station.memoryLevel === 'ok', 'status-unknown': !station.memoryLevel }">
                Memory: {{ station.memoryLevel ? station.memoryLevel.toUpperCase() : 'No Data' }}
              </span>
              <span class="device-stat-badge"
                :class="!station.hasAnySignal ? 'status-unknown' : (station.hasWeakSignal ? 'status-error' : 'status-processed')">
                Signal: {{ !station.hasAnySignal ? 'No Data' : (station.hasWeakSignal ? 'Weak' : 'Strong') }}
              </span>
              <span class="device-stat-badge" v-if="station.firmwareVersions.length">FW: {{ station.firmwareVersions.join(', ') }}</span>
              <span class="device-stat-badge" v-if="station.vpnConfigured" :class="station.vpnDown ? 'status-error' : 'status-processed'">VPN {{ station.vpnDown ? 'Down' : 'Up' }}</span>
              <span class="device-stat-badge status-error" v-if="station.framPending">Sync Pending</span>
            </div>

            <div class="popup-section-label station-card-section-label">Last Transaction</div>
            <div class="nozzle-detail-list" v-if="station.nozzles.length">
              <div class="nozzle-detail-card" v-for="(n, idx) in station.nozzles" :key="idx">
                <div class="nozzle-detail-header">
                  <span class="nozzle-badge">⛽ Nozzle {{ n.noz }} · {{ n.deviceAlias || n.deviceId }}</span>
                  <span class="nozzle-detail-time">{{ n.time }}</span>
                </div>
                <div class="nozzle-detail-body">
                  <div class="nozzle-detail-item">
                    <span class="nozzle-detail-label">Volume</span>
                    <span class="nozzle-detail-value">{{ n.volume }}</span>
                  </div>
                  <div class="nozzle-detail-item" v-if="n.reg">
                    <span class="nozzle-detail-label">Registration</span>
                    <span class="nozzle-detail-value">{{ n.reg }}</span>
                  </div>
                  <div class="nozzle-detail-item" v-if="n.total">
                    <span class="nozzle-detail-label">Totaliser</span>
                    <span class="nozzle-detail-value">{{ n.total }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="no-devices" v-else>No transactions recorded in range.</div>
          </div>

          <div v-if="stationCards.length === 0" class="empty-state">
            <div class="empty-icon">🏬</div>
            <div class="empty-title">No Stations Found</div>
            <div class="empty-description">Try adjusting your filters</div>
          </div>
        </div>
      </div>

      <!-- Search Section -->
      <div class="search-section">
        <div class="filter-header">
          <h3 class="filter-title">Filters</h3>
          <button class="toggle-filter-btn" @click="filtersVisible = !filtersVisible">
            <span class="toggle-icon">{{ filtersVisible ? '📁' : '📂' }}</span>
            {{ filtersVisible ? 'Hide Filters' : 'Show Filters' }}
          </button>
        </div>

        <transition name="slide-fade">
          <div v-if="filtersVisible" class="search-controls-wrapper">
            <div class="search-controls">
              <div class="search-group date-filter-group">
                <label class="search-label">Date <span class="date-range-showing">{{ activeDateRangeLabel }}</span></label>
                <input type="date" class="date-picker-input" v-model="filters.specificDate"
                  :max="getTodayDateString()" @change="onSpecificDateChange" />
              </div>

              <div class="search-group">
                <label class="search-label">Firmware Version <span class="multi-badge" v-if="filters.firmwareVersions.length">({{ filters.firmwareVersions.length }})</span></label>
                <div class="multi-select-container">
                  <div class="multi-select-display" @click="toggleDropdown('firmwareVersion')">
                    <span v-if="filters.firmwareVersions.length === 0" class="placeholder">All Versions</span>
                    <span v-else class="selected-items">
                      {{ filters.firmwareVersions.length }} selected
                    </span>
                    <span class="dropdown-arrow">▼</span>
                  </div>
                  <div v-if="dropdownStates.firmwareVersion" class="multi-select-dropdown">
                    <div class="select-all-option">
                      <label class="checkbox-option">
                        <input type="checkbox" @change="toggleAllFirmwareVersions" :checked="filters.firmwareVersions.length === 0">
                        <span>Select All</span>
                      </label>
                    </div>
                    <div class="dropdown-options">
                      <label class="checkbox-option" v-for="version in uniqueFirmwareVersionsForFilter" :key="version">
                        <input type="checkbox" :value="version" v-model="filters.firmwareVersions" @change="applyFilters">
                        <span>{{ version }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div class="search-group">
                <label class="search-label">Store <span class="multi-badge" v-if="filters.deviceIds.length">({{ filters.deviceIds.length }})</span></label>
                <div class="multi-select-container">
                  <div class="multi-select-display" @click="toggleDropdown('device')">
                    <span v-if="filters.deviceIds.length === 0" class="placeholder">All Devices</span>
                    <span v-else class="selected-items">
                      {{ filters.deviceIds.length }} selected
                    </span>
                    <span class="dropdown-arrow">▼</span>
                  </div>
                  <div v-if="dropdownStates.device" class="multi-select-dropdown">
                    <div class="select-all-option">
                      <label class="checkbox-option">
                        <input type="checkbox" @change="toggleAllDevices" :checked="filters.deviceIds.length === 0">
                        <span>Select All</span>
                      </label>
                    </div>
                    <div class="dropdown-options">
                      <label class="checkbox-option" v-for="device in uniqueDevices" :key="device">
                        <input type="checkbox" :value="device" v-model="filters.deviceIds" @change="applyFilters">
                        <span>{{ device }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div class="search-group">
                <label class="search-label">Memory Health</label>
                <select class="search-select" v-model="filters.memoryHealth" @change="applyFilters">
                  <option value="">All Levels</option>
                  <option value="critical">Critical (&lt;10KB free)</option>
                  <option value="low">Low (&lt;30KB free)</option>
                  <option value="ok">OK (&#8805;30KB free)</option>
                </select>
              </div>

              <!-- Column Selector Dropdown -->
              <div class="search-group">
                <label class="search-label">
                  Visible Columns
                  <span class="multi-badge" v-if="selectedColumns.length !== availableColumns.length">
                    ({{ selectedColumns.length }}/{{ availableColumns.length }})
                  </span>
                </label>
                <div class="multi-select-container">
                  <div class="multi-select-display" @click="toggleDropdown('columns')">
                    <span v-if="selectedColumns.length === availableColumns.length" class="placeholder">All Columns</span>
                    <span v-else class="selected-items">
                      {{ selectedColumns.length }} columns selected
                    </span>
                    <span class="dropdown-arrow">▼</span>
                  </div>
                  <div v-if="dropdownStates.columns" class="multi-select-dropdown">
                    <div class="select-all-option">
                      <label class="checkbox-option">
                        <input type="checkbox" @change="toggleAllColumns" :checked="selectedColumns.length === availableColumns.length">
                        <span>Select All</span>
                      </label>
                    </div>
                    <div class="dropdown-options">
                      <label class="checkbox-option" v-for="column in availableColumns" :key="column.key">
                        <input type="checkbox" :value="column.key" v-model="selectedColumns" @change="handleColumnVisibilityChange">
                        <span>{{ column.label }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="filter-actions">
              <button class="apply-filters-btn" @click="applyFilters">
                Apply Filters
              </button>
              <button class="reset-filters-btn" @click="resetFilters">
                Reset
              </button>
            </div>
          </div>
        </transition>
      </div>

      <!-- Data Grid -->
      <div class="grid-container">
        <div class="grid-header">
          <div class="grid-title">
            IOT Startup Activity Feed
          </div>
          <div class="grid-controls">
          <button
            v-if="hasActiveColumnSearches"
            class="clear-column-searches-btn"
            @click="clearColumnSearches"
          >
            <span class="action-icon">✖</span><span>Clear Searches</span>
          </button>
            <div class="rows-selector">
              <span class="rows-label">Show:</span>
              <select v-model="itemsPerPage" @change="handleRowsChange" class="rows-select">
                <option :value="10">10 rows</option>
                <option :value="15">15 rows</option>
                <option :value="20">20 rows</option>
                <option :value="25">25 rows</option>
                <option :value="50">50 rows</option>
                <option :value="100">100 rows</option>
              </select>
            </div>
            <div class="grid-actions">
              <button class="action-button popout-btn" @click="openInNewWindow" title="Pop Out">
                <span class="btn-icon">⬜</span>
                <span>Pop Out</span>
              </button>
              <button class="action-button refresh-btn" @click="refreshData" title="Refresh">
                <span class="btn-icon">🔄</span>
                <span>Refresh</span>
              </button>
              <button class="action-button export-btn" @click="exportData" title="Export">
                <span class="btn-icon">📥</span>
                <span>Export</span>
              </button>
            </div>
          </div>
        </div>

        <div class="table-container">
          <!-- Loading State -->
          <div v-if="loading" class="loading-overlay">
            <div class="loader"></div>
          </div>

          <!-- Data Table with Dynamic Columns -->
          <table v-if="!loading && filteredActivities.length > 0" class="data-table">
          <thead>
            <tr>
              <th v-for="column in visibleColumns" :key="column.key">
                {{ column.label }}
              </th>
            </tr>
            <!-- NEW: Column search row -->
            <tr class="search-row">
              <th v-for="column in visibleColumns" :key="'s-' + column.key" class="search-cell">
                <input
                  type="text"
                  class="column-search-input"
                  placeholder="Search..."
                  v-model="columnSearchFilters[column.key]"
                  @input="handleColumnSearch"
                />
              </th>
            </tr>
          </thead>
            <tbody>
              <!-- <tr v-for="activity in paginatedActivities" :key="activity.id"> -->
              <tr v-for="activity in paginatedActivities" :key="activity.id" @click="showActivityDetails(activity)" style="cursor: pointer;">
                <!-- Date column -->
                <td v-if="isColumnVisible('timestamp')">
                  <div>{{ formatDateTime(activity.timestamp || activity.createDate) }}</div>
                  <div style="font-size: 0.75rem; color: #6c757d;">
                    {{ getRelativeTime(activity.timestamp || activity.createDate) }}
                  </div>
                </td>

                <!-- Device column -->
                <td v-if="isColumnVisible('deviceId')">
                  <div class="device-info">
                    <span class="device-id">{{ activity.deviceId }}</span>
                    <span class="device-alias">{{ activity.deviceAlias }}</span>
                  </div>
                </td>

                <!-- Type Code column -->
                <td v-if="isColumnVisible('typeCode')">
                  <span style="font-family: monospace; font-weight: 600; color: var(--autumn-primary);">
                    {{ activity.typeCode || activity.typeID }}
                  </span>
                </td>

                <!-- Store Name column -->
                <td v-if="isColumnVisible('storeName')">
                  {{ activity.storeName || '-' }}
                </td>

                <!-- Activity Type column -->
                <td v-if="isColumnVisible('type')">
                  <span :class="['activity-badge', 'badge-' + activity.type]">
                    {{ activity.type }}
                  </span>
                </td>

                <!-- Transaction ID column -->
                <td v-if="isColumnVisible('transactionId')">
                  <code style="font-size: 0.75rem;">{{ activity.transactionId || activity.transactionID }}</code>
                </td>

                <!-- Volume column -->
                <td v-if="isColumnVisible('volume')">
                  <span class="volume-display" v-if="activity.volume">
                    {{ activity.volume.toFixed(2) }}
                  </span>
                  <span v-else style="color: #6c757d;">-</span>
                </td>

                <!-- Details/Message column - UPDATED TO SHOW FULL MESSAGE -->
                <td v-if="isColumnVisible('message')">
                  <div class="message-cell">
                    {{ getTelemetrySummaryLine(activity) }}
                  </div>
                </td>

                <!-- Status column -->
                <td v-if="isColumnVisible('status')">
                  <div class="status-indicator" :class="'status-' + activity.status">
                    <span class="status-dot"></span>
                    {{ activity.status }}
                  </div>
                </td>

                <!-- Additional columns based on availability -->
                <td v-if="isColumnVisible('storeID')">
                  {{ activity.storeID || '-' }}
                </td>

                <td v-if="isColumnVisible('accountID')">
                  {{ activity.accountID || '-' }}
                </td>

                <td v-if="isColumnVisible('accountName')">
                  {{ activity.accountName || '-' }}
                </td>

                <td v-if="isColumnVisible('recordTypeID')">
                  {{ activity.recordTypeID || '-' }}
                </td>

                <td v-if="isColumnVisible('kmReading')">
                  {{ activity.kmReading || '-' }}
                </td>

                <td v-if="isColumnVisible('maxDispenseLitres')">
                  {{ activity.maxDispenseLitres || '-' }}
                </td>

                <td v-if="isColumnVisible('tankNumber')">
                  {{ activity.tankNumber || '-' }}
                </td>

                <td v-if="isColumnVisible('iotDeviceDescription')">
                  {{ activity.iotDeviceDescription || '-' }}
                </td>

                <td v-if="isColumnVisible('iotDeviceTypeID')">
                  {{ activity.iotDeviceTypeID || '-' }}
                </td>

                <td v-if="isColumnVisible('isProcessed')">
                  {{ activity.isProcessed === 1 ? 'Yes' : 'No' }}
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Empty State -->
          <div v-if="!loading && filteredActivities.length === 0" class="empty-state">
            <div class="empty-icon">📂</div>
            <div class="empty-title">No Activities Found</div>
            <div class="empty-description">
              Try adjusting your filters or check back later for new data
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="pagination-container">
          <div class="pagination-info">
            Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
            {{ Math.min(currentPage * itemsPerPage, filteredActivities.length) }}
            of {{ filteredActivities.length }} entries
            <span class="per-page-info">({{ itemsPerPage }} per page)</span>
          </div>
          <div class="pagination-controls">
            <button
              class="page-btn prev-btn"
              @click="currentPage = 1"
              :disabled="currentPage === 1">
              Previous
            </button>
            <button
              v-for="page in visiblePageNumbers"
              :key="page"
              class="page-btn page-number"
              :class="{ 'active': page === currentPage }"
              @click="currentPage = page">
              {{ page }}
            </button>
            <button
              class="page-btn next-btn"
              @click="currentPage++"
              :disabled="currentPage === totalPages">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const MEMORY_THRESHOLDS_BYTES = {
  critical: 10000, // < 10 KB free internal heap
  low: 30000       // < 30 KB free internal heap
};

export default {
  name: 'FamsIotStartupActivitiesDashboard',

  data() {
    return {
      // Add this new property
      selectedActivity: null,

      // Device breakdown popup (Statistics Overview cards drill into this)
      showDeviceBreakdown: false,
      deviceBreakdownTitle: '',
      deviceBreakdownType: '', // 'memory' | 'signal' | 'firmware' - controls which chart (if any) shows per row
      deviceBreakdownRows: [],

      // Station detail popup - full device info for one store (Station Overview cards drill into this)
      showStationDetail: false,
      stationDetail: null,
      // Loader states
      showLoaderOption: true,
      isLoading: false,
      loaderMessage: 'Loading...',
      loaderTimeout: null,
      minLoaderTime: 1500,
      loaderStartTime: null,

      // Auto-refresh settings
      autoRefreshEnabled: false,
      refreshInterval: 30000,
      refreshTimer: null,
      nextRefreshTime: null,

      // Connection and loading states
      connectionStatus: 'Live',
      loading: false,
      lastUpdate: null,
      errorCount: 0,

      // API Configuration
      apiConfig: {
        baseURL: process.env.VUE_APP_API_URL || '',
        accountId: localStorage.getItem('userAccountId'),
        refreshInterval: 15000,
      },

      // Pagination
      currentPage: 1,
      itemsPerPage: 15,

      // Data
      activities: [],
      rawApiData: [],
      uniqueDevices: [],
      uniqueStores: [],

      // Statistics
      stats: {
        totalDevicesWithActivity: 0,
        memoryCritical: 0,
        memoryLow: 0,
        memoryOk: 0,
        memoryUnknown: 0,
        signalWeak: 0,
        signalStrong: 0,
        signalUnknown: 0,
        firmwareVersionCount: 0,
        firmwareVersionCounts: {},
        vpnUp: 0,
        vpnDown: 0,
        vpnMaybeUp: 0,
        vpnConfiguredCount: 0,
        framPending: 0,
        framOk: 0,
        framUnknown: 0,
        withTransactionCount: 0,
        noTransactionCount: 0
      },

      // Filters
      filters: {
        startDateTime: this.getDefaultStartDate(), // Today at 00:00
        endDateTime: this.getDefaultEndDate(),     // Today at 23:59
        firmwareVersions: [],
        deviceIds: [],
        memoryHealth: '',
        specificDate: this.getTodayDateString()
      },
      filtersVisible: true,
      statsVisible: true,

      // Dropdown states for multi-select
      dropdownStates: {
        firmwareVersion: false,
        device: false,
        columns: false
      },

      columnSearchFilters: {},
      // Column management
      availableColumns: [
        { key: 'timestamp', label: 'Date', visible: true },
        { key: 'deviceId', label: 'Device', visible: true },
        { key: 'typeCode', label: 'Type Code', visible: false },
        { key: 'storeName', label: 'Store Name', visible: true },
        { key: 'type', label: 'Activity Type', visible: true },
        { key: 'transactionId', label: 'Transaction ID', visible: true },
        { key: 'volume', label: 'Volume (L)', visible: true },
        { key: 'message', label: 'Details', visible: true },
        { key: 'status', label: 'Status', visible: true },
        { key: 'storeID', label: 'Store ID', visible: false },
        { key: 'accountID', label: 'Account ID', visible: false },
        { key: 'accountName', label: 'Account Name', visible: false },
        { key: 'recordTypeID', label: 'Record Type ID', visible: false },
        { key: 'kmReading', label: 'KM Reading', visible: false },
        { key: 'maxDispenseLitres', label: 'Max Dispense Litres', visible: false },
        { key: 'tankNumber', label: 'Tank Number', visible: false },
        { key: 'iotDeviceDescription', label: 'Device Description', visible: false },
        { key: 'iotDeviceTypeID', label: 'Device Type ID', visible: false },
        { key: 'isProcessed', label: 'Is Processed', visible: false }
      ],

      selectedColumns: ['timestamp', 'deviceId', 'storeName', 'message', 'status'],

      pollingInterval: null
    };
  },

  computed: {
    filteredActivities() {
      let filtered = [...this.activities];

      if (this.filters.startDateTime) {
        const startDate = new Date(this.filters.startDateTime);
        filtered = filtered.filter(a => new Date(a.timestamp || a.createDate) >= startDate);
      }
      if (this.filters.endDateTime) {
        const endDate = new Date(this.filters.endDateTime);
        filtered = filtered.filter(a => new Date(a.timestamp || a.createDate) <= endDate);
      }
      if (this.filters.firmwareVersions.length > 0) {
        filtered = filtered.filter(a => a.telemetryFields && this.filters.firmwareVersions.includes(a.telemetryFields.AppVer));
      }
      if (this.filters.deviceIds.length > 0) {
        filtered = filtered.filter(a => this.filters.deviceIds.includes(a.storeName));
      }
      if (this.filters.memoryHealth) {
        filtered = filtered.filter(a => this.memoryLevel(a.telemetryFields) === this.filters.memoryHealth);
      }

      // Column-level search filters
      if (Object.keys(this.columnSearchFilters).length > 0) {
        filtered = filtered.filter(activity => {
          for (const key in this.columnSearchFilters) {
            const sv = this.columnSearchFilters[key];
            if (!sv || !sv.trim()) continue;
            const search = sv.toLowerCase();

            let val = '';
            if (key === 'timestamp') {
              val = this.formatDateTime(activity.timestamp || activity.createDate) || '';
            } else if (key === 'deviceId') {
              val = ((activity.deviceId || '') + ' ' + (activity.deviceAlias || ''));
            } else if (key === 'typeCode') {
              val = String(activity.typeCode || activity.typeID || '');
            } else if (key === 'transactionId') {
              val = String(activity.transactionId || activity.transactionID || '');
            } else {
              val = String(activity[key] != null ? activity[key] : '');
            }

            if (!val.toLowerCase().includes(search)) return false;
          }
          return true;
        });
      }

      return filtered;
    },

    totalPages() {
      return Math.ceil(this.filteredActivities.length / this.itemsPerPage);
    },

    paginatedActivities() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredActivities.slice(start, end);
    },

    hasActiveColumnSearches() {
      return Object.keys(this.columnSearchFilters).some(k =>
        this.columnSearchFilters[k] && this.columnSearchFilters[k].trim() !== ''
      );
    },

    visiblePageNumbers() {
      const maxVisible = 5;
      const pages = [];
      let start = Math.max(1, this.currentPage - 2);
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    },

    visibleColumns() {
      return this.availableColumns.filter(col => this.selectedColumns.includes(col.key));
    },

    minAllowedDate() {
      const d = new Date();
      d.setDate(d.getDate() - 3);
      return d.toISOString().split('T')[0];
    },

    maxAllowedDate() {
      return new Date().toISOString().split('T')[0];
    },

    activeDateRangeLabel() {
      if (this.filters.specificDate)
        return `Showing: ${this.filters.specificDate} (00:00 – 23:59)`;
      return '';
    },

    uniqueFirmwareVersionsForFilter() {
      const versions = new Set();
      this.activities.forEach(a => {
        if (a.telemetryFields && a.telemetryFields.AppVer) versions.add(a.telemetryFields.AppVer);
      });
      return [...versions].sort();
    },

    // One card per store, mapping every device at that store to its current health
    // (memory/signal/firmware/VPN/sync) and the last transaction recorded on any of
    // its nozzles - so a station's identity, condition, and activity are in one place.
    stationCards() {
      const devices = this.getMergedTelemetryByDevice(this.activities);

      const byStore = {};
      devices.forEach(d => {
        const key = d.storeName || 'Unknown Store';
        if (!byStore[key]) byStore[key] = [];
        byStore[key].push(d);
      });

      const cards = Object.keys(byStore).map(storeName => {
        const storeDevices = byStore[storeName];

        const memoryLevels = storeDevices.map(d => this.memoryLevel(d.fields)).filter(Boolean);
        const memoryLevel = memoryLevels.includes('critical') ? 'critical'
          : memoryLevels.includes('low') ? 'low'
          : memoryLevels.includes('ok') ? 'ok'
          : null;

        const hasAnySignal = storeDevices.some(d => typeof d.fields.WifiRssi === 'number' || this.hasGsmSignal(d.fields));
        const hasWeakSignal = storeDevices.some(d => this.isWeakSignal(d.fields));

        const firmwareVersions = [...new Set(storeDevices.map(d => d.fields.AppVer).filter(Boolean))];

        const vpnStatuses = storeDevices.map(d => d.fields.VpnStatus).filter(Boolean);
        const vpnConfigured = vpnStatuses.length > 0;
        const vpnDown = vpnStatuses.includes('Down');

        const framPending = storeDevices.some(d => typeof d.fields.FramUnposted === 'number' && d.fields.FramUnposted > 0);

        const nozzles = [];
        storeDevices.forEach(d => {
          Object.values(d.lastDispenseByNozzle).forEach(n => {
            nozzles.push({
              deviceId: d.deviceId,
              deviceAlias: d.deviceAlias,
              noz: n.Noz,
              volume: typeof n.Liters === 'number' ? `${n.Liters.toFixed(2)} L` : '-',
              reg: n.Reg || '',
              total: typeof n.Tot === 'number' ? n.Tot.toLocaleString() : '',
              reportedAt: n.reportedAt,
              time: this.formatDateTime(n.reportedAt)
            });
          });
        });
        nozzles.sort((a, b) => new Date(b.reportedAt) - new Date(a.reportedAt));

        const lastUpdatedAt = storeDevices
          .map(d => d.lastUpdated)
          .filter(Boolean)
          .sort((a, b) => new Date(b) - new Date(a))[0] || null;

        return {
          storeName,
          devices: storeDevices.map(d => ({
            deviceId: d.deviceId,
            deviceAlias: d.deviceAlias,
            fields: d.fields,
            lastUpdated: d.lastUpdated,
            nozzles: Object.values(d.lastDispenseByNozzle).sort((a, b) => new Date(b.reportedAt) - new Date(a.reportedAt))
          })),
          memoryLevel,
          hasAnySignal,
          hasWeakSignal,
          firmwareVersions,
          vpnConfigured,
          vpnDown,
          framPending,
          nozzles,
          mostRecentTransactionAt: nozzles.length ? nozzles[0].reportedAt : null,
          lastUpdatedAt
        };
      });

      return cards.sort((a, b) => {
        if (!a.mostRecentTransactionAt && !b.mostRecentTransactionAt) return a.storeName.localeCompare(b.storeName);
        if (!a.mostRecentTransactionAt) return 1;
        if (!b.mostRecentTransactionAt) return -1;
        return new Date(b.mostRecentTransactionAt) - new Date(a.mostRecentTransactionAt);
      });
    }
  },

   // ADD A WATCHER FOR FILTERED ACTIVITIES (optional but recommended)
  watch: {
    filteredActivities: {
      handler: function() {
        // Automatically update stats when filtered data changes
        this.updateStats();
      },
      deep: false
      
    }
  },

  methods: {
  handleColumnSearch() {
  this.currentPage = 1;
    },

  clearColumnSearches() {
    this.columnSearchFilters = {};
    this.currentPage = 1;
    },
 // ADD THESE NEW METHODS FOR DEFAULT DATES
    getDefaultStartDate() {
      const date = new Date();
      date.setHours(0, 0, 0, 0);        // Today at 00:00:00
      // Format for datetime-local input: YYYY-MM-DDTHH:MM
      return this.formatDateForInput(date);
    },

    getDefaultEndDate() {
      const date = new Date();
      date.setHours(23, 59, 59, 999);   // Today at 23:59:59
      // Format for datetime-local input
      return this.formatDateForInput(date);
    },

    getTodayDateString() {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    formatDateForInput(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${year}-${month}-${day}T${hours}:${minutes}`;
    },

    // Fires when the user picks a date from the date picker.
    // Scopes both the client-side filters AND the server request to
    // just that single day (00:00 - 23:59), then re-fetches from the API.
    onSpecificDateChange() {
      if (!this.filters.specificDate) {
        this.filters.specificDate = this.getTodayDateString();
      }
      const [y, m, d] = this.filters.specificDate.split('-');
      const start = new Date(y, m - 1, d, 0, 0, 0, 0);
      const end   = new Date(y, m - 1, d, 23, 59, 59, 999);
      this.filters.startDateTime = this.formatDateForInput(start);
      this.filters.endDateTime   = this.formatDateForInput(end);
      this.currentPage = 1;
      this.loadIotStartupActivities();
    },
     // Add this new method
    showActivityDetails(activity) {
      this.selectedActivity = activity;
    },

    startLoader(message = 'Loading...') {
      if (this.showLoaderOption) {
        this.loaderMessage = message;
        this.isLoading = true;
        this.loaderStartTime = Date.now();

        if (this.loaderTimeout) {
          clearTimeout(this.loaderTimeout);
        }

        this.loaderTimeout = setTimeout(() => {
          console.warn('Loader timeout reached, hiding loader');
          this.stopLoader(true);
        }, 30000);
      }
    },

    stopLoader(forceStop = false) {
      const elapsedTime = this.loaderStartTime ? Date.now() - this.loaderStartTime : 0;
      const remainingTime = Math.max(0, this.minLoaderTime - elapsedTime);

      if (forceStop || remainingTime === 0) {
        this.isLoading = false;
        this.loaderMessage = 'Loading...';
        this.loaderStartTime = null;

        if (this.loaderTimeout) {
          clearTimeout(this.loaderTimeout);
          this.loaderTimeout = null;
        }
      } else {
        setTimeout(() => {
          this.isLoading = false;
          this.loaderMessage = 'Loading...';
          this.loaderStartTime = null;

          if (this.loaderTimeout) {
            clearTimeout(this.loaderTimeout);
            this.loaderTimeout = null;
          }
        }, remainingTime);
      }
    },

    setMinLoaderTime(milliseconds) {
      this.minLoaderTime = Math.max(0, Math.min(5000, milliseconds));
      console.log(`Minimum loader display time set to ${this.minLoaderTime}ms`);
    },

    handleAutoRefreshToggle() {
      if (this.autoRefreshEnabled) {
        this.startAutoRefresh();
        this.showNotification('Success', `Auto-refresh enabled (every ${this.formatRefreshInterval()})`, 'success');
      } else {
        this.stopAutoRefresh();
        this.showNotification('Info', 'Auto-refresh disabled - use manual refresh button', 'primary');
      }
    },

    handleRefreshIntervalChange() {
      if (this.autoRefreshEnabled) {
        this.stopAutoRefresh();
        this.startAutoRefresh();
        this.showNotification('Success', `Refresh interval changed to ${this.formatRefreshInterval()}`, 'success');
      }
    },

    startAutoRefresh() {
      this.stopAutoRefresh();

      if (this.autoRefreshEnabled) {
        console.log(`Starting auto-refresh every ${this.refreshInterval}ms`);

        this.nextRefreshTime = Date.now() + this.refreshInterval;

        this.refreshTimer = setInterval(() => {
          console.log('Auto-refresh triggered');
          this.loadIotStartupActivities();
          this.nextRefreshTime = Date.now() + this.refreshInterval;
        }, this.refreshInterval);
      }
    },

    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
        this.nextRefreshTime = null;
        console.log('Auto-refresh stopped');
      }
    },

    formatRefreshInterval() {
      const seconds = this.refreshInterval / 1000;
      if (seconds < 60) {
        return `${seconds}s`;
      } else {
        const minutes = seconds / 60;
        return minutes < 60 ? `${minutes}m` : `${minutes/60}h`;
      }
    },

    toggleStatistics() {
      this.statsVisible = !this.statsVisible;
      this.savePreferences();
      this.showNotification('Info', this.statsVisible ? 'Statistics shown' : 'Statistics hidden', 'primary');
    },

    toggleFilters() {
      this.filtersVisible = !this.filtersVisible;
      this.savePreferences();
      this.showNotification('Info', this.filtersVisible ? 'Filters shown' : 'Filters hidden', 'primary');
    },

    savePreferences() {
      try {
        const preferences = {
          statsVisible: this.statsVisible,
          filtersVisible: this.filtersVisible,
          autoRefreshEnabled: this.autoRefreshEnabled,
          refreshInterval: this.refreshInterval,
          showLoaderOption: this.showLoaderOption,
          minLoaderTime: this.minLoaderTime,
          itemsPerPage: this.itemsPerPage,
          selectedColumns: this.selectedColumns
        };
        localStorage.setItem('fams-iot-startup-preferences', JSON.stringify(preferences));
      } catch (e) {
        console.log('Could not save preferences:', e);
      }
    },

    loadPreferences() {
      try {
        const saved = localStorage.getItem('fams-iot-startup-preferences');
        if (saved) {
          const preferences = JSON.parse(saved);
          this.statsVisible = preferences.statsVisible !== undefined ? preferences.statsVisible : true;
          this.filtersVisible = preferences.filtersVisible !== undefined ? preferences.filtersVisible : true;
          this.autoRefreshEnabled = preferences.autoRefreshEnabled || false;
          this.refreshInterval = preferences.refreshInterval || 30000;
          this.showLoaderOption = preferences.showLoaderOption !== undefined ? preferences.showLoaderOption : true;
          this.minLoaderTime = preferences.minLoaderTime || 1500;
          this.itemsPerPage = preferences.itemsPerPage || 15;
          if (preferences.selectedColumns) {
            this.selectedColumns = preferences.selectedColumns;
          }
        }
      } catch (e) {
        console.log('Could not load preferences:', e);
      }
    },

    toggleDropdown(type) {
      Object.keys(this.dropdownStates).forEach(key => {
        if (key !== type) {
          this.dropdownStates[key] = false;
        }
      });
      this.dropdownStates[type] = !this.dropdownStates[type];
    },

    toggleAllFirmwareVersions() {
      if (this.filters.firmwareVersions.length === 0) {
        this.filters.firmwareVersions = [...this.uniqueFirmwareVersionsForFilter];
      } else {
        this.filters.firmwareVersions = [];
      }
      this.applyFilters();
    },

    toggleAllDevices() {
      if (this.filters.deviceIds.length === 0) {
        this.filters.deviceIds = [...this.uniqueDevices];
      } else {
        this.filters.deviceIds = [];
      }
      this.applyFilters();
    },

    isColumnVisible(columnKey) {
      return this.selectedColumns.includes(columnKey);
    },

    toggleAllColumns() {
      if (this.selectedColumns.length === this.availableColumns.length) {
        this.selectedColumns = ['timestamp', 'deviceId', 'storeName', 'message', 'status'];
      } else {
        this.selectedColumns = this.availableColumns.map(col => col.key);
      }
      this.savePreferences();
    },

    handleColumnVisibilityChange() {
      this.savePreferences();
      this.showNotification('Success', 'Column visibility updated', 'success');
    },

    formatColumnLabel(key) {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .replace(/Id$/i, 'ID')
        .replace(/Iot/i, 'IOT');
    },

    isJsonMessage(message) {
      if (!message || typeof message !== 'string') return false;
      const trimmed = message.trim();
      return (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
             (trimmed.startsWith('[') && trimmed.endsWith(']'));
    },

    prettyMessage(message) {
      if (!message) return '';
      if (this.isJsonMessage(message)) {
        try {
          return JSON.stringify(JSON.parse(message), null, 2);
        } catch (e) {
          return message;
        }
      }
      return message;
    },

    handleClickOutside(event) {
      const multiSelectContainers = this.$el.querySelectorAll('.multi-select-container');
      let clickedInside = false;

      multiSelectContainers.forEach(container => {
        if (container.contains(event.target)) {
          clickedInside = true;
        }
      });

      if (!clickedInside) {
        Object.keys(this.dropdownStates).forEach(key => {
          this.dropdownStates[key] = false;
        });
      }
    },

    getActivityType(typeCode) {
      const typeMap = {
        3: 'stop',
        8: 'select',
        105: 'limit',
        123: 'action',
        124: 'complete',
        125: 'dispense',
        126: 'driver',
        130: 'odometer',
        205: 'alert',
        402: 'auth',
      };
      return typeMap[typeCode] || 'system';
    },

    extractVolumeFromMessage(message) {
      if (!message) return 0;

      const volumeMatch = message.match(/(\d+(?:\.\d+)?)\s*L/i);
      if (volumeMatch) {
        return parseFloat(volumeMatch[1]);
      }

      const litresMatch = message.match(/(\d+(?:\.\d+)?)\s*litre/i);
      if (litresMatch) {
        return parseFloat(litresMatch[1]);
      }

      return 0;
    },

    generateTransactionId() {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let result = '';
      for (let i = 0; i < 12; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return result;
    },

    async loadIotStartupActivities() {
      var self = this;
      try {
        self.startLoader('Loading IOT data...');

        // Always fetch the full 72-hour window the API allows (regardless of the
        // selected date filter) so device state - VPN, FRAM sync, memory, signal,
        // firmware - keeps its last known value across date changes instead of
        // reading as "no data" just because nothing new was reported on the
        // currently-selected day. The date filter still narrows what's SHOWN in the
        // Data Grid (via filteredActivities); Statistics Overview and Station cards
        // read from this full fetched set (this.activities) instead.
        var end = self.formatDateTime(self.filters.endDateTime);
        var lookbackStart = new Date(self.filters.endDateTime);
        lookbackStart.setHours(lookbackStart.getHours() - 72);
        var start = self.formatDateTime(lookbackStart);

        self.loading = true;
        self.connectionStatus = 'Loading...';

        var onSuccess = function (response) {
          var res = response.data;
          if (response.data) {
            self.rawApiData = res;
            self.processApiData(res);
            self.lastUpdate = new Date();
            self.connectionStatus = 'Live';
            self.errorCount = 0;

         // Show success notification with the selected day
            if (!self.pollingInterval) {
              const dateRangeText = self.filters.specificDate ? ` (${self.filters.specificDate})` : '';
              self.showNotification('Success', `Loaded ${self.filteredActivities.length} activities${dateRangeText}`, 'success');
            }
          }
          self.stopLoader();
        };

        var onFinally = function (res) {
          console.log('IOT data load attempt finished.');
          self.loading = false;
          self.stopLoader();
        };

        self.$ajaxGet(
          self,
          `VwIotdataNotificationComprehensiveActivity/GetIotStartupActivities?accountId=${self.apiConfig.accountId}&startDate=${start}&endDate=${end}`,
          onSuccess,
          onFinally
        );

      } catch (error) {
        console.error('Error loading IOT notifications:', error);
        this.errorCount++;
        this.connectionStatus = 'Connection Error';

        if (this.errorCount >= 3) {
          this.showNotification('Error', 'Failed to load IOT data. Retrying...', 'danger');
        }
        this.loading = false;
        this.stopLoader();
      }
    },

    processApiData(apiData) {
      console.log('Processing API data:', apiData);

      this.activities = apiData.map(item => ({
        id: item.id,
        createDate: item.createDate,
        timestamp: new Date(item.createDate || item.time),

        deviceId: item.deviceId || item.iotDevice_DeviceID,
        deviceAlias: item.deviceAlias || item.telemetry_DeviceAlias,

        storeID: item.storeID,
        storeName: item.storeName,

        accountID: item.accountID,
        accountName: item.accountName,

        recordTypeId: item.recordTypeID || item.telemetry_RecordTypeID,
        recordTypeID: item.recordTypeID || item.telemetry_RecordTypeID,
        typeCode: item.typeID || item.telemetry_Type,
        typeID: item.typeID || item.telemetry_Type,
        type: this.getActivityType(item.telemetry_Type || item.typeID),
        message: item.telemetry_Message || this.extractTelemetrySummary(item) || 'No message',
        transactionId: item.telemetry_TransactionID || this.generateTransactionId(),
        transactionID: item.telemetry_TransactionID || this.generateTransactionId(),

        volume: this.extractVolumeFromMessage(item.telemetry_Message),

        isProcessed: item.isProcessed,
        status: this.determineStatus(item.telemetry_Type || item.typeID, item.isProcessed),

        kmReading: item.telemetry_KmReading,
        maxDispenseLitres: item.telemetry_MaxDispenseLitres,
        tankNumber: item.telemetry_TankNumber,

        iotDeviceDescription: item.iotDevice_Description,
        iotDeviceTypeID: item.iotDevice_TypeID,

        telemetryFields: this.parseTelemetryFields(item),

        timestamp: item.time || new Date(item.createDate).getTime()
      }));

      console.log('Total activities processed:', this.activities.length);
      if (this.activities.length > 0) {
        console.log('Sample activity:', this.activities[0]);

        const sampleItem = this.activities[0];
        Object.keys(sampleItem).forEach(key => {
          if (!this.availableColumns.find(col => col.key === key)) {
            this.availableColumns.push({
              key: key,
              label: this.formatColumnLabel(key),
              visible: false
            });
          }
        });
      }

      this.activities.sort((a, b) => new Date(b.createDate) - new Date(a.createDate));

      this.updateFilterOptions();
      this.updateStats();
    },

    parseTransactionId(message) {
      if (!message) return null;
      const match = message.match(/TrId[:\s]+([A-Za-z0-9]+)/);
      return match ? match[1] : null;
    },

    determineStatus(typeCode, isProcessed) {
      if (isProcessed === 1) return 'processed';
      if (typeCode === 124) return 'processed';
      if (typeCode === 125) return 'pending';
      if (typeCode === 205) return 'error';
      return 'processed';
    },

    updateFilterOptions() {
      const devices = [...new Set(this.activities.map(a => a.storeName).filter(Boolean))];
      this.uniqueDevices = devices;

      const stores = [...new Set(this.activities.map(a => a.storeID).filter(Boolean))];
      this.uniqueStores = stores;
    },

    updateStatsOld() {
      this.stats.totalDevices = this.uniqueDevices.length;
      this.stats.totalStores = this.uniqueStores.length;

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayActivities = this.activities.filter(a =>
        new Date(a.createDate || a.timestamp) >= today
      );
      this.stats.totalActivities = todayActivities.length;

      const volumeSum = this.activities.reduce((sum, a) => sum + (parseFloat(a.volume) || 0), 0);
      this.stats.totalVolume = volumeSum;

      const volumeActivities = this.activities.filter(a => a.volume && a.volume > 0);
      this.stats.avgVolume = volumeActivities.length > 0
        ? (volumeSum / volumeActivities.length)
        : 125.5;

      this.stats.processedCount = this.activities.filter(a => a.status === 'processed').length;
      this.stats.unprocessedCount = this.activities.filter(a => a.status === 'pending').length;

      this.stats.processingRate = this.activities.length > 0
        ? ((this.stats.processedCount / this.activities.length) * 100)
        : 98.5;

      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);
      const yesterdayEnd = new Date(yesterday);
      yesterdayEnd.setHours(23, 59, 59, 999);

      const yesterdayActivities = this.activities.filter(a => {
        const activityDate = new Date(a.createDate || a.timestamp);
        return activityDate >= yesterday && activityDate <= yesterdayEnd;
      });

      if (yesterdayActivities.length > 0) {
        const trendPercent = ((todayActivities.length - yesterdayActivities.length) / yesterdayActivities.length) * 100;
        this.stats.eventsTrend = trendPercent;
      } else {
        this.stats.eventsTrend = todayActivities.length > 10 ? 12.5 : -5.2;
      }
    },

    updateStatsRev1() {
          // Use filtered activities instead of all activities
          const dataToAnalyze = this.filteredActivities;

          // Get unique devices and stores from filtered data
          const uniqueFilteredDevices = [...new Set(dataToAnalyze.map(a => a.deviceId).filter(Boolean))];
          const uniqueFilteredStores = [...new Set(dataToAnalyze.map(a => a.storeID).filter(Boolean))];

          // Update counts based on filtered data
          this.stats.totalDevices = uniqueFilteredDevices.length;
          this.stats.totalStores = uniqueFilteredStores.length;

          // Calculate date range for filtered data
          let todayActivities = [];
          let yesterdayActivities = [];

          if (this.filters.startDateTime || this.filters.endDateTime) {
            // When date filters are active, show total in range instead of "today"
            this.stats.totalActivities = dataToAnalyze.length;

            // For trend calculation when filters are active
            if (dataToAnalyze.length > 0) {
              // Sort by date
              const sortedActivities = [...dataToAnalyze].sort((a, b) =>
                new Date(a.createDate || a.timestamp) - new Date(b.createDate || b.timestamp)
              );

              // Get midpoint to compare first half vs second half
              const midpoint = Math.floor(sortedActivities.length / 2);
              const firstHalf = sortedActivities.slice(0, midpoint);
              const secondHalf = sortedActivities.slice(midpoint);

              // Calculate trend based on first half vs second half
              if (firstHalf.length > 0) {
                const trendPercent = ((secondHalf.length - firstHalf.length) / firstHalf.length) * 100;
                this.stats.eventsTrend = trendPercent;
              } else {
                this.stats.eventsTrend = 0;
              }
            } else {
              this.stats.eventsTrend = 0;
            }
          } else {
            // No date filters - use today's data as before
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            todayActivities = dataToAnalyze.filter(a =>
              new Date(a.createDate || a.timestamp) >= today
            );
            this.stats.totalActivities = todayActivities.length;

            // Yesterday's data for comparison
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            yesterday.setHours(0, 0, 0, 0);
            const yesterdayEnd = new Date(yesterday);
            yesterdayEnd.setHours(23, 59, 59, 999);

            yesterdayActivities = this.activities.filter(a => {
              const activityDate = new Date(a.createDate || a.timestamp);
              return activityDate >= yesterday && activityDate <= yesterdayEnd;
            });

            // Calculate trend
            if (yesterdayActivities.length > 0) {
              const trendPercent = ((todayActivities.length - yesterdayActivities.length) / yesterdayActivities.length) * 100;
              this.stats.eventsTrend = trendPercent;
            } else {
              this.stats.eventsTrend = todayActivities.length > 10 ? 12.5 : -5.2;
            }
          }

          // Volume calculations from filtered data
          const volumeSum = dataToAnalyze.reduce((sum, a) => sum + (parseFloat(a.volume) || 0), 0);
          this.stats.totalVolume = volumeSum;

          // Average volume from filtered data
          const volumeActivities = dataToAnalyze.filter(a => a.volume && a.volume > 0);
          this.stats.avgVolume = volumeActivities.length > 0
            ? (volumeSum / volumeActivities.length)
            : 0;

          // Processing status from filtered data
          this.stats.processedCount = dataToAnalyze.filter(a => a.status === 'processed').length;
          this.stats.unprocessedCount = dataToAnalyze.filter(a => a.status === 'pending').length;

          // Processing rate from filtered data
          this.stats.processingRate = dataToAnalyze.length > 0
            ? ((this.stats.processedCount / dataToAnalyze.length) * 100)
            : 0;
        },
updateStats() {
      // Health/config stats reflect every device ever seen in the loaded data, not just
      // the currently selected date range - a device that hasn't sent anything new
      // recently should still show its last known state rather than disappear.
      const devices = this.getMergedTelemetryByDevice(this.activities);

      // Card totals reflect every device seen, not just the ones with a known reading -
      // any device missing that particular metric falls into its "no data" bucket so
      // the numbers across all three cards always add up to the same device count.
      this.stats.totalDevicesWithActivity = devices.length;

      // Devices by memory health
      this.stats.memoryCritical = devices.filter(d => this.memoryLevel(d.fields) === 'critical').length;
      this.stats.memoryLow = devices.filter(d => this.memoryLevel(d.fields) === 'low').length;
      this.stats.memoryOk = devices.filter(d => this.memoryLevel(d.fields) === 'ok').length;
      this.stats.memoryUnknown = devices.length - this.stats.memoryCritical - this.stats.memoryLow - this.stats.memoryOk;

      // Devices by signal strength
      const weakSignalCount = devices.filter(d => this.isWeakSignal(d.fields)).length;
      const strongSignalCount = devices.filter(d => {
        const f = d.fields;
        const hasSignal = typeof f.WifiRssi === 'number' || this.hasGsmSignal(f);
        return hasSignal && !this.isWeakSignal(f);
      }).length;
      this.stats.signalWeak = weakSignalCount;
      this.stats.signalStrong = strongSignalCount;
      this.stats.signalUnknown = devices.length - weakSignalCount - strongSignalCount;

      // Firmware version spread across devices
      const versionCounts = {};
      devices.forEach(d => {
        const version = d.fields.AppVer;
        if (version) versionCounts[version] = (versionCounts[version] || 0) + 1;
      });
      this.stats.firmwareVersionCounts = versionCounts;
      this.stats.firmwareVersionCount = Object.keys(versionCounts).length;

      // VPN status - only devices with WireGuard configured report this at all
      this.stats.vpnUp = devices.filter(d => d.fields.VpnStatus === 'Up').length;
      this.stats.vpnDown = devices.filter(d => d.fields.VpnStatus === 'Down').length;
      this.stats.vpnMaybeUp = devices.filter(d => d.fields.VpnStatus === 'MaybeUp').length;
      this.stats.vpnConfiguredCount = this.stats.vpnUp + this.stats.vpnDown + this.stats.vpnMaybeUp;

      // Pending sync (FRAM-backed records not yet posted to the server)
      this.stats.framPending = devices.filter(d => typeof d.fields.FramUnposted === 'number' && d.fields.FramUnposted > 0).length;
      this.stats.framOk = devices.filter(d => d.fields.FramUnposted === 0).length;
      this.stats.framUnknown = devices.length - this.stats.framPending - this.stats.framOk;

      // Last transaction (dispense) on record, per device/store
      this.stats.withTransactionCount = devices.filter(d => Object.keys(d.lastDispenseByNozzle).length > 0).length;
      this.stats.noTransactionCount = devices.length - this.stats.withTransactionCount;
    },

    // One row per device holding its CURRENT known state, not just its latest packet.
    // Each StsPkt is a partial update - the device only sends keys that are new or
    // changed since its last send, so a field's absence means "unchanged," not
    // "unknown" or "zero." Layering each packet's fields over the last (in
    // chronological order) reconstructs what the device's state actually is.
    // AppVer/IdfVer/GitIdfVer are always sent, but everything else (heap, uptime,
    // signal, VPN, tanks, dispenses, syncs) may be carried over from an earlier packet.
    getMergedTelemetryByDevice(dataToAnalyze) {
      // Every device with any activity in range gets a row - even ones that never
      // sent structured telemetry (e.g. only plain-text notifications) - so drilldowns
      // show the full device list rather than silently dropping devices with no data.
      const sorted = [...dataToAnalyze]
        .sort((a, b) => new Date(a.createDate || a.timestamp) - new Date(b.createDate || b.timestamp));

      const mergedByDevice = {};
      sorted.forEach(a => {
        const dispenses = (a.telemetryFields && a.telemetryFields.LastDisp) || [];
        const existing = mergedByDevice[a.deviceId];
        if (!existing) {
          mergedByDevice[a.deviceId] = {
            deviceId: a.deviceId,
            deviceAlias: a.deviceAlias,
            storeName: a.storeName,
            fields: a.telemetryFields ? { ...a.telemetryFields } : {},
            lastUpdated: a.createDate || a.timestamp,
            // Keyed by nozzle - a store with multiple dispensing nozzles reports each
            // separately, and a later packet naming only one nozzle shouldn't erase
            // what's known about the others.
            lastDispenseByNozzle: {}
          };
        }
        const device = mergedByDevice[a.deviceId];
        if (existing) {
          if (a.telemetryFields) device.fields = { ...device.fields, ...a.telemetryFields };
          device.deviceAlias = a.deviceAlias || device.deviceAlias;
          device.storeName = a.storeName || device.storeName;
          device.lastUpdated = a.createDate || a.timestamp;
        }
        dispenses.forEach(disp => {
          device.lastDispenseByNozzle[disp.Noz] = { ...disp, reportedAt: a.createDate || a.timestamp };
        });
      });
      return Object.values(mergedByDevice);
    },

    // Tiered classification for HeapFreeInt (bytes) - mirrors the Memory Health filter
    memoryLevel(fields) {
      if (!fields || typeof fields.HeapFreeInt !== 'number') return null;
      if (fields.HeapFreeInt < MEMORY_THRESHOLDS_BYTES.critical) return 'critical';
      if (fields.HeapFreeInt < MEMORY_THRESHOLDS_BYTES.low) return 'low';
      return 'ok';
    },

    getFirmwareSpreadLabel() {
      const counts = this.stats.firmwareVersionCounts || {};
      const versions = Object.keys(counts);
      if (!versions.length) return 'No version data';
      return versions.map(v => `${v}: ${counts[v]}`).join(', ');
    },

    // Maps RSSI (-100 = no signal, -30 = excellent) to a 0-100% bar fill
    // -100 dBm = complete signal loss (0%), 0 dBm = full strength (100%)
    getSignalBarPercent(rssi) {
      if (typeof rssi !== 'number') return 0;
      return Math.min(100, Math.max(0, rssi + 100));
    },

    // Maps free internal heap (bytes) to a 0-100% bar fill, capped at 100KB so the
    // critical/low thresholds stay visually meaningful rather than the bar always
    // looking nearly-full on a device with hundreds of KB free.
    getMemoryBarPercent(heapFreeInt) {
      if (typeof heapFreeInt !== 'number') return 0;
      return Math.min(100, Math.max(0, (heapFreeInt / 100000) * 100));
    },

    openMemoryBreakdown() {
      const devices = this.getMergedTelemetryByDevice(this.activities);
      const levelOrder = { critical: 0, low: 1, ok: 2, unknown: 3 };
      const levelBadgeClass = { critical: 'status-error', low: 'status-pending', ok: 'status-processed', unknown: 'status-unknown' };
      const levelLabel = { critical: 'Critical', low: 'Low', ok: 'OK', unknown: 'No Data' };

      // Show every device, not just the ones with a known level - devices that never
      // reported heap data still get a row so the list reflects all devices, not a subset.
      this.deviceBreakdownRows = devices
        .map(d => {
          const level = this.memoryLevel(d.fields) || 'unknown';
          return {
            deviceId: d.deviceId,
            deviceAlias: d.deviceAlias,
            storeName: d.storeName,
            badgeClass: levelBadgeClass[level],
            badgeLabel: levelLabel[level],
            detailLabel: level === 'unknown' ? '' : this.formatBytes(d.fields.HeapFreeInt) + ' free',
            heapFreeInt: typeof d.fields.HeapFreeInt === 'number' ? d.fields.HeapFreeInt : null,
            _order: levelOrder[level]
          };
        })
        .sort((a, b) => a._order - b._order);

      this.deviceBreakdownTitle = 'Devices by Memory Health';
      this.deviceBreakdownType = 'memory';
      this.showDeviceBreakdown = true;
    },

    openSignalBreakdown() {
      const devices = this.getMergedTelemetryByDevice(this.activities);

      // Show every device - ones with no signal reading yet get a "No Data" row instead
      // of being dropped from the list.
      this.deviceBreakdownRows = devices
        .map(d => {
          const f = d.fields;
          const hasSignal = typeof f.WifiRssi === 'number' || this.hasGsmSignal(f);
          if (!hasSignal) {
            return {
              deviceId: d.deviceId,
              deviceAlias: d.deviceAlias,
              storeName: d.storeName,
              badgeClass: 'status-unknown',
              badgeLabel: 'No Data',
              detailLabel: '',
              wifiRssi: null,
              _order: 2
            };
          }
          const weak = this.isWeakSignal(f);
          const signalParts = [];
          if (typeof f.WifiRssi === 'number') signalParts.push(`WiFi ${f.WifiRssi}dBm`);
          if (this.hasGsmSignal(f)) signalParts.push(`GSM ${f.GsmRssi}dBm`);
          return {
            deviceId: d.deviceId,
            deviceAlias: d.deviceAlias,
            storeName: d.storeName,
            badgeClass: weak ? 'status-error' : 'status-processed',
            badgeLabel: weak ? 'Weak' : 'Strong',
            detailLabel: signalParts.join(' · '),
            wifiRssi: typeof f.WifiRssi === 'number' ? f.WifiRssi : null,
            _order: weak ? 0 : 1
          };
        })
        .sort((a, b) => a._order - b._order);

      this.deviceBreakdownTitle = 'Devices by Signal Strength';
      this.deviceBreakdownType = 'signal';
      this.showDeviceBreakdown = true;
    },

    openFirmwareBreakdown() {
      const devices = this.getMergedTelemetryByDevice(this.activities);

      // Show every device - ones with no version reported yet still get a row.
      this.deviceBreakdownRows = devices
        .map(d => ({
          deviceId: d.deviceId,
          deviceAlias: d.deviceAlias,
          storeName: d.storeName,
          badgeClass: d.fields.AppVer ? 'status-processed' : 'status-unknown',
          badgeLabel: d.fields.AppVer ? `v${d.fields.AppVer}` : 'Unknown',
          detailLabel: d.fields.IdfVer ? `IDF ${d.fields.IdfVer}` : ''
        }))
        .sort((a, b) => a.badgeLabel.localeCompare(b.badgeLabel));

      this.deviceBreakdownTitle = 'Devices by Firmware Version';
      this.deviceBreakdownType = 'firmware';
      this.showDeviceBreakdown = true;
    },

    openVpnBreakdown() {
      const devices = this.getMergedTelemetryByDevice(this.activities);
      const statusOrder = { Down: 0, MaybeUp: 1, Up: 2, none: 3 };
      const statusBadgeClass = { Down: 'status-error', MaybeUp: 'status-pending', Up: 'status-processed', none: 'status-unknown' };
      const statusLabel = { Down: 'Down', MaybeUp: 'Maybe Up', Up: 'Up', none: 'No VPN' };

      // Devices without WireGuard configured simply never report VpnStatus at all -
      // that's expected, not missing data, so they get a distinct "No VPN" badge
      // rather than the "no data" wording used for genuinely unreported metrics.
      this.deviceBreakdownRows = devices
        .map(d => {
          const status = d.fields.VpnStatus || 'none';
          return {
            deviceId: d.deviceId,
            deviceAlias: d.deviceAlias,
            storeName: d.storeName,
            badgeClass: statusBadgeClass[status],
            badgeLabel: statusLabel[status],
            detailLabel: d.fields.VpnIp || '',
            _order: statusOrder[status]
          };
        })
        .sort((a, b) => a._order - b._order);

      this.deviceBreakdownTitle = 'Devices by VPN Status';
      this.deviceBreakdownType = 'vpn';
      this.showDeviceBreakdown = true;
    },

    openFramBreakdown() {
      const devices = this.getMergedTelemetryByDevice(this.activities);

      this.deviceBreakdownRows = devices
        .map(d => {
          const unposted = d.fields.FramUnposted;
          if (typeof unposted !== 'number') {
            return {
              deviceId: d.deviceId,
              deviceAlias: d.deviceAlias,
              storeName: d.storeName,
              badgeClass: 'status-unknown',
              badgeLabel: 'No Data',
              detailLabel: '',
              _order: 2
            };
          }
          const pending = unposted > 0;
          return {
            deviceId: d.deviceId,
            deviceAlias: d.deviceAlias,
            storeName: d.storeName,
            badgeClass: pending ? 'status-error' : 'status-processed',
            badgeLabel: pending ? 'Pending' : 'Synced',
            detailLabel: `${unposted} unposted`,
            _order: pending ? 0 : 1
          };
        })
        .sort((a, b) => a._order - b._order);

      this.deviceBreakdownTitle = 'Devices by Pending Sync';
      this.deviceBreakdownType = 'fram';
      this.showDeviceBreakdown = true;
    },

    openLastTransactionBreakdown() {
      const devices = this.getMergedTelemetryByDevice(this.activities);

      // LastDisp carries no timestamp of its own - the packet that reported it is the
      // closest available proxy for "when this device last recorded a dispense." A
      // store can have multiple dispensing nozzles, each reported independently, so
      // show every nozzle's own last transaction rather than only the most recent one.
      this.deviceBreakdownRows = devices
        .map(d => {
          const nozzles = Object.values(d.lastDispenseByNozzle)
            .sort((a, b) => new Date(b.reportedAt) - new Date(a.reportedAt));

          if (nozzles.length === 0) {
            return {
              deviceId: d.deviceId,
              deviceAlias: d.deviceAlias,
              storeName: d.storeName,
              badgeClass: 'status-unknown',
              badgeLabel: 'No Data',
              detailLabel: '',
              _order: new Date(0)
            };
          }

          const mostRecentAt = nozzles[0].reportedAt;
          const nozzleDetails = nozzles.map(n => ({
            noz: n.Noz,
            volume: typeof n.Liters === 'number' ? `${n.Liters.toFixed(2)} L` : '-',
            reg: n.Reg || '',
            total: typeof n.Tot === 'number' ? n.Tot.toLocaleString() : '',
            time: this.formatDateTime(n.reportedAt)
          }));

          return {
            deviceId: d.deviceId,
            deviceAlias: d.deviceAlias,
            storeName: d.storeName,
            badgeClass: 'status-processed',
            badgeLabel: this.formatDateTime(mostRecentAt),
            nozzleDetails,
            _order: new Date(mostRecentAt)
          };
        })
        .sort((a, b) => b._order - a._order);

      this.deviceBreakdownTitle = 'Last Transaction by Device';
      this.deviceBreakdownType = 'lastTransaction';
      this.showDeviceBreakdown = true;
    },

    openStationDetail(station) {
      this.stationDetail = station;
      this.showStationDetail = true;
    },

    // Some startup activities carry no Telemetry_Message (e.g. version-report or
    // heap/uptime payloads that use a structured "obj"/property bag instead of "msg").
    // Fall back to the raw notification JSON so those still show something useful.
    extractTelemetrySummary(item) {
      if (!item.telemetryDataRaw) return null;
      try {
        const parsed = JSON.parse(item.telemetryDataRaw);
        const entries = parsed.TelementryData || parsed.TelemetryData;
        const entry = entries && entries[0];
        if (!entry) return null;
        const { Type, ...rest } = entry;
        return Object.keys(rest).length ? JSON.stringify(rest) : null;
      } catch (e) {
        return null;
      }
    },

    // Normalized field bag for structured display/health checks - handles both the
    // nested "obj" shape (version reports) and the flat property shape (heap/uptime reports).
    parseTelemetryFields(item) {
      if (!item.telemetryDataRaw) return null;
      try {
        const parsed = JSON.parse(item.telemetryDataRaw);
        const entries = parsed.TelementryData || parsed.TelemetryData;
        const entry = entries && entries[0];
        if (!entry) return null;

        // StsPkt (Type 12) content: current firmware sends it as msg (object); some
        // captured payloads used obj instead. Type 1 plain-text notifications carry
        // msg as a STRING (no structured fields), and a few older payloads put fields
        // directly on the entry with no wrapper at all.
        let fields;
        if (entry.msg && typeof entry.msg === 'object') {
          fields = entry.msg;
        } else if (entry.obj && typeof entry.obj === 'object') {
          fields = entry.obj;
        } else {
          const { Type, msg, ...rest } = entry;
          fields = rest;
        }

        return Object.keys(fields).length ? fields : null;
      } catch (e) {
        return null;
      }
    },

    isLowMemory(fields) {
      const level = this.memoryLevel(fields);
      return level === 'critical' || level === 'low';
    },

    isWeakSignal(fields) {
      if (!fields) return false;
      const wifi = typeof fields.WifiRssi === 'number' ? fields.WifiRssi : null;
      const gsm = this.hasGsmSignal(fields) ? fields.GsmRssi : null;
      return (wifi !== null && wifi <= -75) || (gsm !== null && gsm <= -95);
    },

    // GsmRssi uses 0 and -255 as "no reading" sentinels (modem disabled/not connected -
    // seen paired with GsmOper: "" and GsmTech: 0) rather than real signal strength.
    hasGsmSignal(fields) {
      return !!fields && typeof fields.GsmRssi === 'number' && fields.GsmRssi !== 0 && fields.GsmRssi !== -255;
    },

    // GsmOper sometimes arrives with literal embedded quote characters (e.g. "\"VodaCom-SA\"")
    cleanGsmOper(value) {
      return typeof value === 'string' ? value.replace(/^"+|"+$/g, '') : value;
    },

    getGsmTechLabel(tech) {
      const labels = { 0: 'No Service', 1: 'GSM', 2: 'GPRS', 3: 'LTE', 4: 'LTE+' };
      return labels[tech] !== undefined ? labels[tech] : `Unknown (${tech})`;
    },

    formatUptime(seconds) {
      if (typeof seconds !== 'number') return '-';
      const total = Math.floor(seconds);
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      if (h > 0) return `${h}h ${m}m`;
      if (m > 0) return `${m}m ${s}s`;
      return `${s}s`;
    },

    formatBytes(value) {
      if (typeof value !== 'number') return '-';
      if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(2)} MB`;
      if (value >= 1024) return `${(value / 1024).toFixed(1)} KB`;
      return `${value} B`;
    },

    getTelemetrySummaryLine(activity) {
      const f = activity.telemetryFields;
      if (!f) return activity.message;

      const parts = [];
      if (f.AppVer) parts.push(`App v${f.AppVer}`);
      if (f.IdfVer) parts.push(`IDF ${f.IdfVer}`);
      if (typeof f.Uptime === 'number') parts.push(`Up ${this.formatUptime(f.Uptime)}`);
      if (typeof f.HeapFreeInt === 'number') parts.push(`Heap ${this.formatBytes(f.HeapFreeInt)} free`);
      if (typeof f.WifiRssi === 'number') parts.push(`WiFi ${f.WifiRssi}dBm`);
      if (this.hasGsmSignal(f)) parts.push(`GSM ${f.GsmRssi}dBm`);

      return parts.length ? parts.join(' · ') : activity.message;
    },

    formatDateTime(date) {
      const d = new Date(date);

      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const seconds = String(d.getSeconds()).padStart(2, '0');

      return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
    },

    getRelativeTime(date) {
      const seconds = Math.floor((new Date() - new Date(date)) / 1000);

      if (seconds < 60) return 'Just now';
      if (seconds < 3600) return Math.floor(seconds / 60) + ' minutes ago';
      if (seconds < 86400) return Math.floor(seconds / 3600) + ' hours ago';
      if (seconds < 604800) return Math.floor(seconds / 86400) + ' days ago';

      return new Date(date).toLocaleDateString();
    },

    showNotification(title, text, type = 'primary') {
      if (this.$vs && this.$vs.notify) {
        this.$vs.notify({
          title: title,
          text: text,
          color: type,
          position: 'top-right'
        });
      } else {
        console.log(`${title}: ${text}`);
      }
    },

    applyFiltersOld() {
      this.startLoader('Applying filters...');
      this.currentPage = 1;

      setTimeout(() => {
        this.showNotification('Success', 'Filters applied', 'success');
        this.stopLoader();
      }, 500);
    },

     applyFilters() {
      this.startLoader('Applying filters...');
      this.currentPage = 1;

      // Update statistics based on filtered data
      this.updateStats();

      // Update the stat card label based on filter state
      if (this.filters.startDateTime || this.filters.endDateTime) {
        // You can optionally update the label dynamically
        // This would require making the label reactive
      }

      setTimeout(() => {
        this.showNotification('Success', 'Filters applied', 'success');
        this.stopLoader();
      }, 500);
    },

    resetFilters() {
      this.startLoader('Resetting filters...');

      this.columnSearchFilters = {};

      this.filters = {
        startDateTime: this.getDefaultStartDate(), // Reset to today 00:00
        endDateTime: this.getDefaultEndDate(),     // Reset to today 23:59
        firmwareVersions: [],
        deviceIds: [],
        memoryHealth: '',
        specificDate: this.getTodayDateString()
      };

      Object.keys(this.dropdownStates).forEach(key => {
        if (key !== 'columns') {
          this.dropdownStates[key] = false;
        }
      });

      this.currentPage = 1;
      this.loadIotStartupActivities();

      setTimeout(() => {
        this.showNotification('Success', 'Filters reset to today', 'success');
        this.stopLoader();
      }, 300);
    },

    async refreshData() {
      this.startLoader('Refreshing data...');
      this.showNotification('Info', 'Refreshing data...', 'primary');

      try {
        await this.loadIotStartupActivities();
      } catch (error) {
        console.error('Error refreshing data:', error);
        this.stopLoader();
      }
    },

    exportData() {
      this.startLoader('Exporting data...');
      this.showNotification('Info', 'Exporting data...', 'primary');

      setTimeout(() => {
        const headers = this.visibleColumns.map(col => col.label);

        const csvRows = this.filteredActivities.map(activity => {
          return this.visibleColumns.map(col => {
            let value = '';
            switch(col.key) {
              case 'timestamp':
                value = this.formatDateTime(activity.timestamp || activity.createDate);
                break;
              case 'deviceId':
                value = activity.deviceId;
                break;
              case 'volume':
                value = activity.volume || '';
                break;
              case 'message':
                value = `"${this.getTelemetrySummaryLine(activity) || ''}"`;
                break;
              default:
                value = activity[col.key] || '';
            }
            return value;
          }).join(',');
        });

        const csvContent = [headers.join(','), ...csvRows].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `fams_iot_startup_data_${new Date().getTime()}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);

        this.showNotification('Success', 'Data exported successfully', 'success');
        this.stopLoader();
      }, 1000);
    },

    startPolling() {
      console.warn('startPolling is deprecated. Use auto-refresh toggle instead.');
    },

    stopPolling() {
      console.warn('stopPolling is deprecated. Use auto-refresh toggle instead.');
    },

    handleRowsChange() {
      this.currentPage = 1;
      this.showNotification('Success', `Showing ${this.itemsPerPage} rows per page`, 'success');
    },

    openInNewWindow() {
      this.startLoader('Opening in new window...');

      const columnHeaders = this.visibleColumns.map(col => `<th>${col.label}</th>`).join('');

      const tableRows = this.filteredActivities.map(activity => {
        const cells = this.visibleColumns.map(col => {
          let cellContent = '';
          switch(col.key) {
            case 'timestamp':
              cellContent = this.formatDateTime(activity.timestamp || activity.createDate);
              break;
            case 'deviceId':
              cellContent = `${activity.deviceId}<br><small style="color: #999;">${activity.deviceAlias || ''}</small>`;
              break;
            case 'typeCode':
              cellContent = `<span style="font-family: monospace; color: #D2691E;">${activity.typeCode || activity.typeID}</span>`;
              break;
            case 'type':
              cellContent = `<span class="badge badge-${activity.type}">${activity.type}</span>`;
              break;
            case 'transactionId':
              cellContent = `<span style="font-family: monospace; font-size: 11px;">${activity.transactionId || activity.transactionID}</span>`;
              break;
            case 'volume':
              cellContent = activity.volume ? activity.volume.toFixed(2) : '-';
              break;
            case 'message':
              cellContent = this.getTelemetrySummaryLine(activity) || '-';
              break;
            case 'status':
              cellContent = `<span class="status-${activity.status}">${activity.status}</span>`;
              break;
            case 'isProcessed':
              cellContent = activity.isProcessed === 1 ? 'Yes' : 'No';
              break;
            default:
              cellContent = activity[col.key] || '-';
          }
          return `<td>${cellContent}</td>`;
        }).join('');
        return `<tr>${cells}</tr>`;
      }).join('');

      const popupHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>FAMS IOT Startup Activity Feed</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              margin: 0;
              padding: 20px;
              background: #f5f5f5;
            }
            .header {
              background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
              color: white;
              padding: 20px;
              border-radius: 8px;
              margin-bottom: 20px;
            }
            h1 { margin: 0; font-size: 24px; }
            .info { margin-top: 10px; font-size: 14px; opacity: 0.9; }
            .container {
              background: white;
              border-radius: 8px;
              padding: 20px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              overflow-x: auto;
            }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            th, td {
              padding: 10px;
              text-align: left;
              border-bottom: 1px solid #e0e0e0;
            }
            th {
              background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
              color: white;
              font-weight: 600;
              position: sticky;
              top: 0;
            }
            tr:hover { background: #f8f9fa; }
            .badge {
              display: inline-block;
              padding: 3px 8px;
              border-radius: 4px;
              font-size: 11px;
              font-weight: 600;
            }
            .badge-dispense { background: #3498DB; color: white; }
            .badge-complete { background: #27AE60; color: white; }
            .badge-action { background: #9B59B6; color: white; }
            .badge-stop { background: #E74C3C; color: white; }
            .badge-limit { background: #E67E22; color: white; }
            .badge-auth { background: #3498DB; color: white; }
            .badge-select { background: #95A5A6; color: white; }
            .badge-odometer { background: #16A085; color: white; }
            .badge-driver { background: #8E44AD; color: white; }
            .badge-alert { background: #C0392B; color: white; }
            .badge-system { background: #34495E; color: white; }
            .status-processed { color: #27AE60; }
            .status-pending { color: #F39C12; }
            .status-error { color: #E74C3C; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>FAMS IOT Startup Activity Feed</h1>
            <div class="info">
              <div>Generated: ${new Date().toLocaleString()}</div>
              <div>Total Records: ${this.filteredActivities.length}</div>
              <div>Last Update: ${this.lastUpdate ? new Date(this.lastUpdate).toLocaleString() : 'N/A'}</div>
            </div>
          </div>
          <div class="container">
            <table>
              <thead>
                <tr>${columnHeaders}</tr>
              </thead>
              <tbody>${tableRows}</tbody>
            </table>
          </div>
        </body>
        </html>
      `;

      const popupWindow = window.open('', 'FAMS_IOT_Startup_Activities',
        'width=1400,height=800,resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=yes');

      if (popupWindow) {
        popupWindow.document.write(popupHTML);
        popupWindow.document.close();
        this.showNotification('Success', 'Opened in new window', 'success');
      } else {
        this.showNotification('Warning', 'Please allow pop-ups for this site', 'warning');
      }

      this.stopLoader();
    },

    simulateRealTimeUpdate() {
      setInterval(() => {
        const random = Math.random();
        if (random > 0.95) {
          this.connectionStatus = 'Updating...';
          setTimeout(() => {
            this.connectionStatus = 'Live';
          }, 1000);
        }
      }, 10000);
    }
  },

  mounted() {
    this.loadPreferences();
    // IMPORTANT: Set default dates if not already set by preferences
    if (!this.filters.specificDate) {
      this.filters.specificDate = this.getTodayDateString();
    }
    if (!this.filters.startDateTime) {
      this.filters.startDateTime = this.getDefaultStartDate();
    }
    if (!this.filters.endDateTime) {
      this.filters.endDateTime = this.getDefaultEndDate();
    }
    this.loadIotStartupActivities();

    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    }

    this.simulateRealTimeUpdate();

    document.addEventListener('click', this.handleClickOutside);

    setTimeout(() => {
      const refreshStatus = this.autoRefreshEnabled
        ? `Auto-refresh enabled (every ${this.formatRefreshInterval()})`
        : 'Auto-refresh is disabled by default';
        this.showNotification('Welcome!', `FAMS Device Startup Activity Dashboard - Showing current date data - ${refreshStatus}`, 'success');
    }, 500);
  },

  beforeDestroy() {
    this.stopAutoRefresh();

    if (this.loaderTimeout) {
      clearTimeout(this.loaderTimeout);
      this.loaderTimeout = null;
    }

    document.removeEventListener('click', this.handleClickOutside);
  }
}
</script>

<style>
/* All your existing styles plus the new message cell style */
:root {
  --fams-primary: #2C3E50;
  --fams-secondary: #3498DB;
  --fams-accent: #E74C3C;
  --fams-success: #27AE60;
  --fams-warning: #F39C12;
  --fams-danger: #E74C3C;
  --fams-info: #3498DB;
  --fams-dark: #34495E;
  --fams-light: #ECF0F1;

  --autumn-primary: #D2691E;
  --autumn-secondary: #CD853F;
  --autumn-accent: #FF8C00;
  --autumn-warm: #FFA500;
  --autumn-deep: #8B4513;
  --autumn-light: #FFE4B5;
  --autumn-gold: #DAA520;
  --autumn-rust: #B22222;

  --gradient-primary: linear-gradient(135deg, var(--autumn-primary) 0%, var(--autumn-accent) 100%);
  --gradient-data: linear-gradient(135deg, var(--fams-secondary) 0%, var(--fams-info) 100%);
  --gradient-success: linear-gradient(135deg, var(--fams-success) 0%, #2ECC71 100%);
  --gradient-warning: linear-gradient(135deg, var(--fams-warning) 0%, #F1C40F 100%);
  --gradient-danger: linear-gradient(135deg, var(--fams-danger) 0%, #C0392B 100%);

  --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow-base: 0 2px 8px rgba(0, 0, 0, 0.1);
  --shadow-md: 0 4px 16px rgba(0, 0, 0, 0.15);
  --shadow-lg: 0 10px 30px rgba(0, 0, 0, 0.2);
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  /* background: linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%); */
  background: #e2e8f0; /* Darker grey background */
  height: 100vh;
  overflow: hidden;
  width: 100%; /* Ensure full width */
  margin: 0; /* Remove default margins */
  padding: 0; /* Remove default padding */
}

#app {
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
   max-width: none;
}

/* NEW: Message cell styling to show full content */
.message-cell {
  min-width: 250px;
  max-width: 500px;
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
  line-height: 1.4;
  padding: 4px 0;
}

/* Rest of your existing styles... */
.header-toolbar {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  padding: 16px 0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* max-width: 1400px;
  margin: 0 auto; */
  width: 100%;
  padding: 0 20px;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-icon {
  width: 40px;
  height: 40px;
  background: white;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: var(--autumn-primary);
}

.dashboard-title {
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.loader-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.loader-toggle {
  display: flex;
  align-items: center;
}

.toggle-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.toggle-checkbox {
  width: 18px;
  height: 18px;
  margin-right: 8px;
  cursor: pointer;
  accent-color: white;
}

.toggle-text {
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
}

.loader-speed {
  display: flex;
  align-items: center;
}

.speed-label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.speed-text {
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
}

.speed-select {
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.speed-select:hover {
  background: rgba(255, 255, 255, 0.3);
}

.speed-select:focus {
  outline: none;
  border-color: white;
}

.speed-select option {
  background: #D2691E;
  color: white;
}

.auto-refresh-toggle {
  display: flex;
  align-items: center;
  padding-left: 15px;
  border-left: 1px solid rgba(255, 255, 255, 0.3);
}

.refresh-interval {
  display: flex;
  align-items: center;
}

.interval-label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.interval-text {
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
}

.interval-select {
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.interval-select:hover {
  background: rgba(255, 255, 255, 0.3);
}

.interval-select:focus {
  outline: none;
  border-color: white;
}

.interval-select option {
  background: #D2691E;
  color: white;
}

.live-dot.auto-refresh {
  background: #4CAF50;
  animation: pulse 1s infinite;
}

.global-loader-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(2px);
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.global-loader-content {
  background: white;
  border-radius: 16px;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.global-loader {
  width: 60px;
  height: 60px;
  border: 4px solid #FFE4B5;
  border-top: 4px solid #D2691E;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loader-text {
  color: #34495E;
  font-size: 1rem;
  font-weight: 500;
  margin-top: 10px;
}

.live-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.2);
  padding: 8px 16px;
  border-radius: 20px;
}

.live-dot {
  width: 8px;
  height: 8px;
  background: #4CAF50;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

.live-dot.error {
  background: #f44336;
  animation: pulse-error 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

@keyframes pulse-error {
  0% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(244, 67, 54, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
  }
}

.main-content {
  flex: 1;
  overflow: visible;
  padding: 20px;
  width: 100%;
   background: #e2e8f0; /* Darker grey background - RESTORED */
  position: relative; /* Ensure proper stacking context */
}

.stats-section {
  background: #ffffff; /* White card */
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15), 0 1px 2px rgba(0, 0, 0, 0.1);
  overflow: visible; /* Allow dropdowns to show if needed */
  width: 100%;
  border: 1px solid #cbd5e1;
}

.stats-header {
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
  border-radius: 12px 12px 0 0; /* Maintain rounded top corners */
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stats-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
  margin: 0;
}

.toggle-stats-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* Add shadow for depth */
}

.toggle-stats-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(210, 105, 30, 0.3);
}

.stats-wrapper {
  /* padding: 16px; */
  padding: 16px 20px; /* Increased horizontal padding */
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.stat-card {
   background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border-left: 4px solid #D2691E;
  border: 1px solid #e2e8f0; /* Match main background for subtle border */
  position: relative;
  z-index: 1;
  overflow: visible;
}

.stat-card * {
  position: relative;
  z-index: 2;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  background: #ffffff; /* Pure white on hover */
}

.stat-label {
  color: #2c3e50 !important;
  font-size: 0.875rem;
  margin-bottom: 8px;
  font-weight: 600;
  position: relative;
  z-index: 2;
  display: block;
  line-height: 1.2;
}

.stat-value {
  font-size: 1.75rem;
  color: #2c3e50 !important;
  font-weight: bold;
  background: linear-gradient(135deg, #FFFFFF 0%, #FF8C00 100%);
  -webkit-background-clip: text;
  background-clip: text;
}

.stat-change {
  font-size: 0.75rem;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat-change .stat-icon {
  font-size: 0.875rem;
}

.stat-change.positive {
  color: var(--fams-success);
}

.stat-change.negative {
  color: var(--fams-danger);
}

.search-section {
  background: #ffffff; /* White card */
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15), 0 1px 2px rgba(0, 0, 0, 0.1);
  overflow: visible; /* Allow dropdowns to show */
  width: 100%;
  border: 1px solid #cbd5e1;
}

/* Add a higher z-index to the entire search section when a dropdown is open */
.search-section:has(.multi-select-dropdown) {
  z-index: 100;
  position: relative;
}

.search-section.dropdown-open {
  z-index: 100;
  position: relative;
}

.multi-select-container {
   position: relative;
  overflow: visible;
}

.multi-select-display {
  padding: 10px 12px;
  border: 1px solid #94a3b8; /* Darker border */
  border-radius: 8px;
  background: #ffffff; /* Pure white */
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
  min-height: 44px;
}

.multi-select-display:hover {
  border-color: #D2691E;
  background: #fff8f3; /* Slight warm tint on hover */
}

.multi-select-display .placeholder {
  color: #999;
}

.multi-select-display .selected-items {
  color: #495057;
  font-weight: 500;
}

.dropdown-arrow {
  color: #999;
  font-size: 0.75rem;
  transition: transform 0.3s ease;
}

.multi-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #D2691E;
  border-radius: 8px;
  margin-top: 4px;
  max-height: 250px;
  overflow-y: auto;
  z-index: 9999; /* Very high z-index to ensure it appears above everything */
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.select-all-option {
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  background: #f8f9fa;
}

.dropdown-options {
  max-height: 200px;
  overflow-y: auto;
}

.checkbox-option {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.checkbox-option:hover {
  background: #fff3e0;
}

.checkbox-option input[type="checkbox"] {
  margin-right: 8px;
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #D2691E;
}

.checkbox-option span {
  font-size: 0.875rem;
  color: #495057;
  user-select: none;
}

.multi-badge {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 4px;
}

.dropdown-options::-webkit-scrollbar {
  width: 6px;
}

.dropdown-options::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.dropdown-options::-webkit-scrollbar-thumb {
  background: #D2691E;
  border-radius: 3px;
}

.dropdown-options::-webkit-scrollbar-thumb:hover {
  background: #8B4513;
}

.filter-header {
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
  border-radius: 12px 12px 0 0; /* Maintain rounded top corners */
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
  margin: 0;
}

.toggle-filter-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* Add shadow for depth */
}

.toggle-filter-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(210, 105, 30, 0.3);
}

.toggle-icon {
  font-size: 1rem;
}

.search-controls-wrapper {
  padding: 16px 20px;
  position: relative;
  overflow: visible;
}

.search-controls {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
  overflow: visible;
  position: relative;
}

.search-group {
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: visible;
}

.search-label {
  font-size: 0.875rem;
  color: #34495E;
  margin-bottom: 4px;
  font-weight: 500;
}

.search-input, .search-select {
  padding: 10px 12px;
  border: 1px solid #94a3b8; /* Darker border */
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: #ffffff; /* Pure white */
}

.search-input:focus, .search-select:focus {
   outline: none;
  border-color: #D2691E;
  box-shadow: 0 0 0 3px rgba(210, 105, 30, 0.15);
  background: #ffffff;
}

.filter-actions {
  display: flex;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
  position: relative;
  z-index: 1;
}

.apply-filters-btn {
  padding: 10px 24px;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.apply-filters-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(210, 105, 30, 0.3);
}

.reset-filters-btn {
  padding: 10px 24px;
  background: white;
  color: #34495E;
  border: 2px solid #FFE4B5;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.reset-filters-btn:hover {
  background: #FFE4B5;
}

.slide-fade-enter-active {
    transition: all 0.3s ease;
  overflow: visible; /* Allow overflow during animation */
}

.slide-fade-leave-active {
    transition: all 0.3s ease;
  overflow: visible; /* Allow overflow during animation */
}

.slide-fade-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.grid-container {
  background: #ffffff; /* White card */
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15), 0 1px 2px rgba(0, 0, 0, 0.1);
  overflow: hidden; /* Keep hidden for table scrolling */
  width: 100%;
  border: 1px solid #cbd5e1;
}

.grid-header {
  padding: 16px 20px;
  background: #f1f5f9; /* Medium grey */
  border-bottom: 1px solid #cbd5e1;
  border-radius: 12px 12px 0 0; /* Maintain rounded top corners */
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.grid-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
}

.grid-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.rows-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rows-label {
  font-size: 0.875rem;
  color: #6c757d;
  font-weight: 500;
}

.rows-select {
   padding: 6px 12px;
  border: 1px solid #94a3b8;
  border-radius: 6px;
  background: white;
  color: #475569;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 100px;
}

.rows-select:hover {
  border-color: #D2691E;
}

.rows-select:focus {
  outline: none;
  border-color: #D2691E;
  box-shadow: 0 0 0 3px rgba(210, 105, 30, 0.1);
}

.grid-actions {
  display: flex;
  gap: 8px;
}

.action-button {
  padding: 8px 14px;
  background: white;
  border: 1px solid #94a3b8; /* Darker border */
  border-radius: 6px;
  color: #475569;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.action-button:hover {
 background: #f8fafc;
  border-color: #D2691E;
  color: #D2691E;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.action-button.popout-btn:hover {
  background: #e3f2fd;
  border-color: #2196F3;
  color: #2196F3;
}

.action-button.refresh-btn:hover {
  background: #e8f5e9;
  border-color: #4CAF50;
  color: #4CAF50;
}

.action-button.export-btn:hover {
  background: #fff3e0;
  border-color: #FF9800;
  color: #FF9800;
}

.btn-icon {
  font-size: 1rem;
}

.action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.table-container {
  overflow: auto;
  position: relative;
  max-height: 500px;
  width: 100%;
  background: #ffffff; /* White background for table area */
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  position: sticky;
  top: 0;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  z-index: 10;
}

.data-table th {
  padding: 12px 16px;
  text-align: left;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.data-table tbody tr {
   border-bottom: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  background: #ffffff;
}

.data-table tbody tr:hover {
   background: #f1f5f9; /* Darker grey on hover */
  cursor: pointer;
}

/* Alternating row colors with more contrast */
.data-table tbody tr:nth-child(even) {
  background: #f8fafc;
}

.data-table tbody tr:nth-child(even):hover {
  background: #f1f5f9;
}

.data-table td {
  padding: 12px 16px;
  font-size: 0.875rem;
  color: var(--fams-dark);
}

.activity-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.badge-dispense {
  background: linear-gradient(135deg, #3498DB, #2980B9);
  color: white;
}

.badge-complete {
  background: linear-gradient(135deg, #27AE60, #229954);
  color: white;
}

.badge-action {
  background: linear-gradient(135deg, #9B59B6, #8E44AD);
  color: white;
}

.badge-stop {
  background: linear-gradient(135deg, #E74C3C, #C0392B);
  color: white;
}

.badge-limit {
  background: linear-gradient(135deg, #E67E22, #D35400);
  color: white;
}

.badge-auth {
  background: linear-gradient(135deg, #3498DB, #2E86AB);
  color: white;
}

.badge-select {
  background: linear-gradient(135deg, #95A5A6, #7F8C8D);
  color: white;
}

.badge-odometer {
  background: linear-gradient(135deg, #16A085, #138D75);
  color: white;
}

.badge-driver {
  background: linear-gradient(135deg, #8E44AD, #6C3483);
  color: white;
}

.badge-alert {
  background: linear-gradient(135deg, #C0392B, #A93226);
  color: white;
}

.badge-system {
  background: linear-gradient(135deg, #34495E, #2C3E50);
  color: white;
}

.status-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-processed {
  background: rgba(39, 174, 96, 0.1);
  color: var(--fams-success);
}

.status-pending {
  background: rgba(243, 156, 18, 0.1);
  color: var(--fams-warning);
}

.status-error {
  background: rgba(231, 76, 60, 0.1);
  color: var(--fams-danger);
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.device-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.device-id {
  font-weight: 600;
  color: var(--fams-dark);
}

.device-alias {
  font-size: 0.75rem;
  color: var(--fams-secondary);
}

.volume-display {
  font-weight: 600;
  color: var(--autumn-primary);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--fams-dark);
  background: #f8fafc; /* Light grey for empty state */
  border-radius: 8px;
  margin: 20px;
  border: 1px solid #e2e8f0;
}

.empty-icon {
  font-size: 4rem;
  color: var(--autumn-light);
  margin-bottom: 16px;
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.empty-description {
  color: #6c757d;
  text-align: center;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(241, 245, 249, 0.95); /* Darker grey overlay */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.loader {
  width: 48px;
  height: 48px;
  border: 4px solid var(--autumn-light);
  border-top: 4px solid var(--autumn-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

code {
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
}

.pagination-container {
  padding: 16px 20px;
  border-top: 1px solid #cbd5e1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f1f5f9; /* Medium grey */
}

.pagination-info {
  color: #6c757d;
  font-size: 0.875rem;
}

.per-page-info {
  color: #D2691E;
  font-weight: 500;
}

.pagination-controls {
  display: flex;
  gap: 4px;
}

.page-btn {
  padding: 6px 12px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  color: #495057;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  min-width: 40px;
}

.page-btn:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #D2691E;
  color: #D2691E;
}

.page-btn.active {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border-color: #D2691E;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.prev-btn, .next-btn {
  min-width: 70px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .main-content {
    padding: 12px;
  }

  .stats-container {
    grid-template-columns: 1fr 1fr;
  }

  .search-controls {
    grid-template-columns: 1fr;
  }

  .data-table {
    font-size: 0.75rem;
  }

  .data-table th, .data-table td {
    padding: 8px;
  }

  .activity-badge {
    font-size: 0.625rem;
    padding: 2px 6px;
  }

  .header-controls {
    flex-wrap: wrap;
  }

  .grid-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .grid-actions {
    justify-content: space-between;
  }

  /* Adjust message cell for mobile */
  .message-cell {
    min-width: 150px;
    max-width: 250px;
    font-size: 0.75rem;
  }
}

/* Additional responsive adjustments for mobile */
@media (max-width: 480px) {
  .header-content {
    flex-direction: column;
    gap: 15px;
  }

  .loader-controls {
    flex-wrap: wrap;
    gap: 10px;
  }

  .stats-container {
    grid-template-columns: 1fr;
  }

  .pagination-container {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }

  .pagination-controls {
    justify-content: center;
  }

  .page-btn {
    min-width: 30px;
    padding: 4px 8px;
    font-size: 0.75rem;
  }

  /* Further adjust message cell for small screens */
  .message-cell {
    min-width: 100px;
    max-width: 200px;
  }
}

/* Large screens optimization for message cell */
@media (min-width: 1400px) {
  .message-cell {
    max-width: 600px;
  }
}

/* Simple Popup Styles */
.simple-popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.popup-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border-radius: 8px 8px 0 0;
}

.popup-header h3 {
  margin: 0;
  font-size: 1.25rem;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 28px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.popup-body {
  padding: 20px;
}

.popup-section-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #34495E;
  margin: 18px 0 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e2e8f0;
}

.message-text {
  margin-top: 8px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 4px;
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-wrap;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.8125rem;
  max-height: 320px;
  overflow-y: auto;
}

.message-text-plain {
  font-family: inherit;
  font-size: 0.875rem;
}

.message-detail-row {
  display: flex;
  flex-direction: column;
}

.telemetry-details-grid {
  margin-top: 8px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 4px;
}

.telemetry-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.telemetry-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #6c757d;
}

.telemetry-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2c3e50;
}

.telemetry-value.telemetry-warning {
  color: #E74C3C;
}

.telemetry-lastdisp {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-top: 1px solid #e0e0e0;
  padding-top: 8px;
  margin-top: 4px;
}

.telemetry-lastdisp-row {
  font-size: 0.8125rem;
  color: #2c3e50;
}

/* Per-nozzle transaction cards (Last Transaction breakdown) */
.nozzle-detail-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}

/* Station Overview cards */
.station-cards-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
  padding: 20px;
}

.station-card {
  border: 2px solid #FF8C00;
  border-radius: 8px;
  padding: 16px;
  background: #fff;
}

.station-card-clickable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.station-card-clickable:hover {
  border-color: #D2691E;
  box-shadow: 0 2px 8px rgba(210, 105, 30, 0.15);
}

.station-card-updated {
  font-size: 0.75rem;
  color: #94a3b8;
  font-style: italic;
  margin-bottom: 10px;
}

.station-card-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.station-card-header strong {
  font-size: 1rem;
  color: #D2691E;
  margin-right: 4px;
}

.station-card-badges {
  margin-bottom: 4px;
}

.station-card-section-label {
  margin: 14px 0 0;
}

.nozzle-detail-card {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 10px;
  background: #f8fafc;
}

.nozzle-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e2e8f0;
}

.nozzle-badge {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #2c3e50;
}

.nozzle-detail-time {
  font-size: 0.75rem;
  color: #64748b;
}

.nozzle-detail-body {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
}

.nozzle-detail-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.nozzle-detail-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #6c757d;
}

.nozzle-detail-value {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #2c3e50;
}

/* Make table rows hover effect more obvious */
.data-table tbody tr:hover {
  background: rgba(210, 105, 30, 0.1);
  cursor: pointer;
}

@media (min-width: 2000px) {
  .header-content {
    max-width: 1920px;
    margin: 0 auto;
  }

  .main-content {
    max-width: 1920px;
    margin: 0 auto;
  }
}

@media (max-width: 768px) {
  .main-content {
    padding: 12px;
  }

  .header-content {
    padding: 0 12px;
  }

  .stats-header,
  .filter-header,
  .grid-header,
  .stats-wrapper,
  .search-controls-wrapper,
  .pagination-container {
    padding-left: 12px;
    padding-right: 12px;
  }
}

/* Clickable stat card */
.clickable-stat {
  cursor: pointer;
  transition: all 0.3s ease;
}

.clickable-stat:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  background: #ffffff !important;
  border-color: #D2691E;
}

/* Device list container */
.device-list-container {
  max-height: 400px;
  overflow-y: auto;
}

/* Device list item */
.device-list-item {
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
  transition: background 0.2s ease;
}

.device-list-item:hover {
  background: #f8fafc;
}

.device-list-item:last-child {
  border-bottom: none;
}

/* Device info layout */
.device-main-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.device-main-info strong {
  color: #D2691E;
  font-size: 1rem;
}

.device-alias {
  color: #64748b;
  font-size: 0.875rem;
  font-style: italic;
}

/* Device stats */
.device-stats {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* Per-device WiFi signal / heap usage mini bar charts */
.device-charts {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
}

.device-chart-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.device-chart-label {
  font-size: 0.75rem;
  color: #64748b;
  min-width: 130px;
  flex-shrink: 0;
}

.device-chart-bar-track {
  flex: 1;
  height: 8px;
  background: #f1f5f9;
  border-radius: 4px;
  overflow: hidden;
}

.device-chart-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.device-chart-bar-fill.bar-strong,
.device-chart-bar-fill.bar-ok {
  background: #27AE60;
}

.device-chart-bar-fill.bar-low {
  background: #F39C12;
}

.device-chart-bar-fill.bar-weak,
.device-chart-bar-fill.bar-critical {
  background: #E74C3C;
}

.device-stat-badge {
  display: inline-block;
  padding: 3px 8px;
  background: #f1f5f9;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.device-stat-badge.volume {
  background: #e0f2fe;
  color: #0369a1;
  border-color: #7dd3fc;
}

.device-stat-badge.status-processed {
  background: #dcfce7;
  color: #14532d;
  border-color: #86efac;
}

.device-stat-badge.status-pending {
  background: #fef3c7;
  color: #78350f;
  border-color: #fde047;
}

.device-stat-badge.status-error {
  background: #fee2e2;
  color: #7f1d1d;
  border-color: #fca5a5;
}

.device-stat-badge.status-unknown {
  background: #f1f5f9;
  color: #64748b;
  border-color: #cbd5e1;
}

/* No devices message */
.no-devices {
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
  font-style: italic;
}

/* Visual hint for clickable card */
.clickable-stat::after {
  content: "📋";
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 0.875rem;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.clickable-stat:hover::after {
  opacity: 0.7;
}

/* Scrollbar styling for device list */
.device-list-container::-webkit-scrollbar {
  width: 6px;
}

.device-list-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.device-list-container::-webkit-scrollbar-thumb {
  background: #D2691E;
  border-radius: 3px;
}

.device-list-container::-webkit-scrollbar-thumb:hover {
  background: #8B4513;
}

/* Volume summary section */
.volume-summary {
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  border: 1px solid #e2e8f0;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.summary-item:last-child {
  margin-bottom: 0;
}

.summary-label {
  color: #64748b;
  font-size: 0.875rem;
}

.summary-value {
  font-weight: 600;
  color: #D2691E;
  font-size: 1rem;
}

/* Volume list container */
.volume-list-container {
  max-height: 400px;
  overflow-y: auto;
}

.volume-list-container h4 {
  margin: 0 0 12px 0;
  color: #334155;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Volume item */
.volume-item {
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
  transition: background 0.2s ease;
}

.volume-item:hover {
  background: #f8fafc;
}

.volume-item:last-child {
  border-bottom: none;
}

/* Volume device info */
.volume-device-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.device-name {
  display: flex;
  align-items: center;
  gap: 8px;
}

.device-name strong {
  color: #1e293b;
  font-size: 0.875rem;
}

/* Volume amount */
.volume-amount {
  display: flex;
  align-items: center;
  gap: 6px;
}

.volume-value {
  font-weight: 700;
  color: #D2691E;
  font-size: 1rem;
}

.volume-percentage {
  color: #64748b;
  font-size: 0.75rem;
}

/* Volume bar visualization */
.volume-bar-container {
  width: 100%;
  height: 20px;
  background: #f1f5f9;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 8px;
}

.volume-bar {
  height: 100%;
  background: linear-gradient(90deg, #3498DB 0%, #2196F3 100%);
  transition: width 0.3s ease;
  border-radius: 10px;
}

/* Volume details */
.volume-details {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.detail-badge {
  display: inline-block;
  padding: 3px 8px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 0.7rem;
  color: #475569;
}

/* No volume message */
.no-volume {
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
  font-style: italic;
}

/* Visual water drop animation for volume card */
.clickable-stat:has(.stat-label:contains("Volume"))::after {
  content: "💧";
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 0.875rem;
  opacity: 0;
  transition: opacity 0.3s ease;
  animation: water-drop 2s infinite;
}

@keyframes water-drop {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}

.clickable-stat:has(.stat-label:contains("Volume")):hover::after {
  opacity: 0.7;
}

/* Scrollbar styling for volume list */
.volume-list-container::-webkit-scrollbar {
  width: 6px;
}

.volume-list-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.volume-list-container::-webkit-scrollbar-thumb {
  background: #3498DB;
  border-radius: 3px;
}

.volume-list-container::-webkit-scrollbar-thumb:hover {
  background: #2196F3;
}

/* Events summary section */
.events-summary {
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  border: 1px solid #e2e8f0;
}

/* Events list container */
.events-list-container {
  max-height: 450px;
  overflow-y: auto;
}

.events-list-container h4 {
  margin: 0 0 12px 0;
  color: #334155;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Events device item */
.events-device-item {
  margin-bottom: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.events-device-item:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Device header */
.device-header {
  padding: 12px;
  background: white;
  cursor: pointer;
  transition: background 0.2s ease;
}

.device-header:hover {
  background: #f8fafc;
}

.device-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.device-name-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.device-name-section strong {
  color: #D2691E;
  font-size: 0.9rem;
}

.device-count {
  display: flex;
  align-items: center;
  gap: 8px;
}

.event-count-badge {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.expand-icon {
  color: #64748b;
  font-size: 0.75rem;
  transition: transform 0.2s ease;
}

/* Device progress bar */
.device-progress-bar {
  width: 100%;
  height: 6px;
  background: #f1f5f9;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #D2691E 0%, #FF8C00 100%);
  transition: width 0.3s ease;
}

/* Device events detail */
.device-events-detail {
  padding: 12px;
  background: #fafbfc;
  border-top: 1px solid #e2e8f0;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Event type grid */
.event-type-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}

.event-type-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.event-type-item .activity-badge {
  font-size: 0.65rem;
  padding: 2px 6px;
}

.event-count {
  font-weight: 600;
  color: #334155;
  font-size: 0.875rem;
}

/* Device timeline */
.device-timeline {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 8px;
}

.timeline-item {
  display: flex;
  flex-direction: column;
  padding: 6px;
  background: white;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
}

.timeline-label {
  color: #64748b;
  font-size: 0.7rem;
  text-transform: uppercase;
}

.timeline-value {
  color: #1e293b;
  font-size: 0.75rem;
  font-weight: 500;
}

/* No events message */
.no-events {
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
  font-style: italic;
}

/* Scrollbar styling */
.events-list-container::-webkit-scrollbar {
  width: 6px;
}

.events-list-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.events-list-container::-webkit-scrollbar-thumb {
  background: #D2691E;
  border-radius: 3px;
}

.events-list-container::-webkit-scrollbar-thumb:hover {
  background: #FF8C00;
}

.search-row {
  background: #fff3e0 !important;
}

.search-row th {
  background: transparent !important;
  padding: 6px !important;
  border-right: 1px solid #ffcc80 !important;
}

.column-search-input {
  width: 100%;
  padding: 5px 8px;
  border: 1px solid #ffcc80;
  border-radius: 4px;
  font-size: 0.75rem;
  background: white;
  color: #333;
  transition: all 0.2s;
  font-weight: normal;
  letter-spacing: normal;
  text-transform: none;
  box-sizing: border-box;
}

.column-search-input:focus {
  outline: none;
  border-color: #ff9800;
  box-shadow: 0 0 0 2px rgba(255, 152, 0, 0.2);
}

.column-search-input::placeholder {
  color: #999;
  font-style: italic;
}

.clear-column-searches-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #ff6b6b 0%, #ff5252 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.clear-column-searches-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(255, 82, 82, 0.4);
}

/* Date Range Filter Styling */
.date-filter-group {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 8px 6px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
}

.date-range-showing {
  font-size: 0.75rem;
  font-weight: 400;
  color: #64748b;
  text-transform: none;
  letter-spacing: 0;
  display: inline-block;
  margin-left: 6px;
  font-weight: normal;
}

.date-quick-buttons {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.date-quick-btn {
  padding: 5px 10px;
  border: 1px solid #cbd5e1;
  background: white;
  border-radius: 5px;
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.date-quick-btn:hover {
  border-color: #D2691E;
  color: #D2691E;
  background: #fff9f5;
}

.date-quick-btn.active {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border-color: #D2691E;
  box-shadow: 0 2px 8px rgba(210, 105, 30, 0.2);
}

.date-picker-input {
  padding: 6px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 5px;
  font-size: 0.85rem;
  color: #334155;
  background: white;
  cursor: pointer;
  transition: all .2s ease;
}

.date-picker-input:hover {
  border-color: #D2691E;
}

.date-picker-input:focus {
  outline: none;
  border-color: #D2691E;
  box-shadow: 0 0 0 2px rgba(210, 105, 30, .2);
}
</style>